// Game Configuration
const CONFIG = {
    // Canvas
    CANVAS_WIDTH: 1200,
    CANVAS_HEIGHT: 640,  // ~20% smaller than 800

    // World dimensions (2 viewports tall, very wide for 10-20 min gameplay)
    WORLD_WIDTH: 5600,  // Wide horizontal world (30% smaller)
    WORLD_HEIGHT: 1440, // 2x viewport height (10% smaller)

    // Player
    PLAYER_SIZE: 26, // Bigger than following family members (22)
    BASE_SPEED: 2,

    // Life and aging system
    BASE_LIFESPAN: 62, // years of life to deplete (80 - 18 = 62 years from 18 to 80)
    GAMEPLAY_DURATION: 165, // seconds (2.75 minutes to go from 18 to ~80 years)
    STARTING_AGE: 18, // Jane starts at age 18

    // Blur system
    BLUR_THRESHOLD_1: 5,   // seconds of continuous movement
    BLUR_THRESHOLD_2: 10,  // seconds
    BLUR_THRESHOLD_3: 15,  // seconds
    BLUR_CLEAR_TIME: 3,    // seconds of standing still to clear

    // Trail
    TRAIL_SPACING: 40,     // Distance between followers
    TRAIL_FOLLOW_SPEED: 0.1,

    // Encounters (as % of world width for horizontal placement)
    PARTNER_ENCOUNTER: 0.30,
    CHILD1_ENCOUNTER: 0.38,
    CHILD2_ENCOUNTER: 0.46,

    // Death events (based on age)
    PARENT1_DEATH_AGE: 32, // Parent 1 dies when Jane is 32
    PARENT2_DEATH_AGE: 36, // Parent 2 dies when Jane is 36
    PARTNER_DEATH_AGE: 64, // Partner dies when Jane is 64

    // Aging rate modifiers (positive = slower aging = longer life)
    PARTNER_AGING_BONUS: 0.10, // 10% slower aging with partner
    CHILD_AGING_BONUS: 0.05, // 5% slower aging per child
    EXPERIENCE_AGING_BONUS: 0.01, // 1% slower aging per experience
    MAX_EXPERIENCE_BONUS: 0.20, // Cap at 20% total from experiences

    // Grief aging penalties (negative = faster aging = shorter life)
    UNPROCESSED_PARENT_GRIEF: -0.15, // 15% faster aging per unprocessed parent
    PROCESSED_PARENT_GRIEF: -0.05, // 5% faster aging per processed parent
    UNPROCESSED_PARTNER_GRIEF: -0.25, // 25% faster aging for unprocessed partner
    PROCESSED_PARTNER_GRIEF: -0.10, // 10% faster aging for processed partner

    // Experiences
    EXPERIENCE_COUNT: 30,
    EXPERIENCE_INTERACT_DISTANCE: 48, // Increased by 20% (was 40)
    EXPERIENCE_COLLECT_TIME: 1, // seconds

    // Colors - Jane's palette (warm coral-peach that harmonizes with green/red)
    COLOR_JANE: '#FF9F7A',           // Warm coral-peach (harmonious base)
    COLOR_JANE_HAPPY: '#7DCEA0',    // Warm sage green (harmonious with coral)
    COLOR_JANE_SAD: '#E85A71',      // Warm coral-red (harmonious with coral)
    // Neutral outline is now a darker shade of COLOR_JANE (calculated dynamically)

    // Family colors
    COLOR_MOM: '#B88DC4',           // Warm lavender-purple
    COLOR_DAD: '#5DADE2',           // Soft sky blue
    COLOR_PARTNER: '#7DCEA0',       // Soft sage green
    COLOR_CHILD: '#F9D56E',         // Warm golden yellow

    // Experience colors
    COLOR_EXPERIENCE_NATURE: '#6BCF7F',
    COLOR_EXPERIENCE_KNOWLEDGE: '#6B8CFF',
    COLOR_EXPERIENCE_PLEASURE: '#FF8C6B',
    COLOR_BACKGROUND: '#F5F5DC',
};

// Game Data - All content, names, labels, and experience definitions
// This file contains all the data that can be easily modified without touching game logic

const GAME_DATA = {
    // Character Names
    names: {
        parent1: 'Mom',
        parent2: 'Dad',
        partner: 'Taylor',
        child1Pool: ['Alex', 'Sam'],
        child2Pool: ['Jordan', 'Riley'],
        // Fallback names if pools are empty
        childFallback: ['Alex', 'Sam', 'Jordan', 'Riley']
    },

    // Memory strings for grave visits (randomized per visit)
    memories: {
        parent1: [
            'baking cookies together',
            'her warm embrace',
            'the sound of her laugh',
            'Sunday morning breakfasts',
            'her gentle wisdom',
            'the songs she used to sing'
        ],
        parent2: [
            'teaching me to ride a bike',
            'his terrible jokes',
            'weekend adventures',
            'his steady presence',
            'the way he hummed while working',
            'his proud smile'
        ],
        partner: [
            'our first dance',
            'lazy Sunday mornings',
            'the way they made me laugh',
            'growing old together',
            'their hand in mine',
            'our quiet moments'
        ]
    },

    // Experience achievements
    experienceAchievements: {
        nature: {
            3: 'a nature lover',
            5: 'one with nature'
        },
        knowledge: {
            3: 'well-read',
            5: 'a scholar'
        },
        pleasure: {
            3: 'learned to savor life',
            5: 'mastered the art of living'
        }
    },

    // Experience Definitions
    experiences: {
        nature: {
            label: 'Nature',
            possibleNames: [
                'went for a walk',
                'watched a sunset',
                'climbed a mountain',
                'visited a lake',
                'stargazed',
                'gardened',
                'hiked in the woods',
                'saw the ocean',
                'watched birds',
                'picked wildflowers',
                'camped under stars',
                'walked in rain',
                'saw a rainbow',
                'visited a park',
                'watched clouds',
                'fed animals'
            ]
        },
        knowledge: {
            label: 'Knowledge',
            possibleNames: [
                'read a novel',
                'learned a language',
                'visited a museum',
                'attended a lecture',
                'studied philosophy',
                'watched a documentary',
                'learned an instrument',
                'took a course',
                'read poetry',
                'solved puzzles',
                'learned to code',
                'studied history',
                'read biographies',
                'attended a workshop',
                'learned to paint',
                'explored science'
            ]
        },
        pleasure: {
            label: 'Pleasure',
            possibleNames: [
                'shared a meal',
                'listened to music',
                'danced',
                'traveled abroad',
                'tried new food',
                'celebrated',
                'relaxed',
                'played a game',
                'watched a movie',
                'had coffee with friends',
                'went to a concert',
                'read in a cafe',
                'took a nap',
                'went shopping',
                'had a picnic',
                'watched a play'
            ]
        }
    },

    // UI Labels and Text
    labels: {
        endScreen: {
            title: 'A Life Remembered',
            peopleSection: 'The People You Loved',
            experiencesSection: 'The Experiences You Cherished',
            journeySection: 'Your Journey',
            parentsLabel: 'Parents',
            partnerLabel: 'Husband',
            childLabel: 'Child',
            childrenLabel: 'Children',
            memoriesRevisited: 'Memories revisited: {count} times'
        },
        memoryView: {
            title: 'Memories',
            noMemories: 'No memories yet.',
            memoryOf: 'Memory {current} of {total}',
            navigationHint: '←/A and →/D to navigate, Space/E/click to revisit, M/ESC to exit'
        },
        encounter: {
            childLabel: 'Child'
        },
        person: {
            partnerUnjoined: 'Husband'
        }
    }
};

// Utility functions

function distance(x1, y1, x2, y2) {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}

function lerp(start, end, t) {
    return start + (end - start) * t;
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function randomRange(min, max) {
    return Math.random() * (max - min) + min;
}

function randomInt(min, max) {
    return Math.floor(randomRange(min, max));
}

// Helper function to darken a hex color
function darkenColor(hex, percent) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const num = parseInt(hex, 16);
    const r = Math.max(0, Math.floor((num >> 16) * (1 - percent)));
    const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) * (1 - percent)));
    const b = Math.max(0, Math.floor((num & 0x0000FF) * (1 - percent)));
    
    // Convert back to hex with padding
    const rHex = r.toString(16).length === 1 ? '0' + r.toString(16) : r.toString(16);
    const gHex = g.toString(16).length === 1 ? '0' + g.toString(16) : g.toString(16);
    const bHex = b.toString(16).length === 1 ? '0' + b.toString(16) : b.toString(16);
    
    return '#' + rHex + gHex + bHex;
}

// Input handling
const Input = {
    keys: {},
    mouseX: 0, // Mouse position in screen coordinates
    mouseY: 0,
    mouseWorldX: 0, // Mouse position in world coordinates
    mouseWorldY: 0,
    mouseClicked: false, // Flag for mouse click (reset after checking)
    camera: null, // Reference to camera for coordinate conversion
    inputMode: null, // 'keyboard' or 'mouse' - set by game

    init(canvas, camera) {
        this.camera = camera;
        
        window.addEventListener('keydown', (e) => {
            this.keys[e.key.toLowerCase()] = true;

            // Prevent arrow keys from scrolling the page
            if(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
                e.preventDefault();
            }
        });

        window.addEventListener('keyup', (e) => {
            this.keys[e.key.toLowerCase()] = false;
        });

        // Track mouse position
        canvas.addEventListener('mousemove', (e) => {
            const rect = canvas.getBoundingClientRect();
            this.mouseX = e.clientX - rect.left;
            this.mouseY = e.clientY - rect.top;
            this.updateWorldCoordinates();
        });

        // Track mouse click and update position
        canvas.addEventListener('click', (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            this.mouseX = e.clientX - rect.left;
            this.mouseY = e.clientY - rect.top;
            this.updateWorldCoordinates();
            this.mouseClicked = true;
        });

        // Track right-click (contextmenu) - treat as Escape key
        canvas.addEventListener('contextmenu', (e) => {
            e.preventDefault(); // Prevent context menu
            // Set escape key as pressed (will be cleared by game after handling)
            this.keys['escape'] = true;
        });
    },

    updateWorldCoordinates() {
        if (this.camera) {
            // Convert screen coordinates to world coordinates
            this.mouseWorldX = this.camera.x + this.mouseX;
            this.mouseWorldY = this.camera.y + this.mouseY;
        }
    },

    setCamera(camera) {
        this.camera = camera;
    },
    
    setInputMode(mode) {
        this.inputMode = mode;
    },

    isDown(key) {
        return this.keys[key] || false;
    },

    isInteracting() {
        return this.isDown(' ') || this.isDown('e') || this.mouseClicked;
    },

    consumeClick() {
        // Check and reset mouse click flag
        const clicked = this.mouseClicked;
        this.mouseClicked = false;
        return clicked;
    }
};

// Camera system for following the player
class Camera {
    constructor(worldWidth, worldHeight, viewWidth, viewHeight) {
        this.x = 0;
        this.y = 0;
        this.worldWidth = worldWidth;
        this.worldHeight = worldHeight;
        this.viewWidth = viewWidth;
        this.viewHeight = viewHeight;
    }

    follow(targetX, targetY) {
        // Center camera on target
        this.x = targetX - this.viewWidth / 2;
        this.y = targetY - this.viewHeight / 2;

        // Clamp to world bounds
        this.x = clamp(this.x, 0, this.worldWidth - this.viewWidth);
        this.y = clamp(this.y, 0, this.worldHeight - this.viewHeight);
    }

    worldToScreen(worldX, worldY) {
        return {
            x: worldX - this.x,
            y: worldY - this.y
        };
    }
}

// Tile Map System
class TileMap {
    constructor(worldWidth, worldHeight, tileSize) {
        this.tileSize = tileSize;
        this.cols = Math.ceil(worldWidth / tileSize);
        this.rows = Math.ceil(worldHeight / tileSize);
        this.tiles = [];

        // Four seasons with multiple tile types each
        this.tileTypes = {
            // SPRING (bloom, greener pastures)
            spring_grass1: { color: '#9CCC65', season: 'spring' }, // Light green
            spring_grass2: { color: '#7CB342', season: 'spring' }, // Medium green
            spring_meadow: { color: '#AED581', season: 'spring' }, // Bright green meadow
            
            // SUMMER (most colorful, bright sunshine)
            summer_field1: { color: '#FFD54F', season: 'summer' }, // Golden yellow
            summer_field2: { color: '#FFB74D', season: 'summer' }, // Warm orange
            summer_garden: { color: '#FFCC80', season: 'summer' }, // Bright peach
            
            // AUTUMN (yellowish leaves)
            autumn_leaves1: { color: '#FFA726', season: 'autumn' }, // Orange
            autumn_leaves2: { color: '#FF8A65', season: 'autumn' }, // Red-orange
            autumn_ground: { color: '#D7CCC8', season: 'autumn' }, // Light brown
            
            // WINTER (blues, greys)
            winter_snow1: { color: '#CFD8DC', season: 'winter' }, // Light grey-blue
            winter_snow2: { color: '#B0BEC5', season: 'winter' }, // Medium grey-blue
            winter_frost: { color: '#90A4AE', season: 'winter' }, // Darker grey-blue
            
            border: { color: '#555555' } // Dark grey border
        };

        this.generateMap();
    }

    generateMap() {
        // Generate map with four seasons
        for (let row = 0; row < this.rows; row++) {
            this.tiles[row] = [];
            for (let col = 0; col < this.cols; col++) {
                // First and last row/column are border tiles
                if (row === 0 || row === this.rows - 1 || col === 0 || col === this.cols - 1) {
                    this.tiles[row][col] = {
                        type: 'border',
                        x: col * this.tileSize,
                        y: row * this.tileSize
                    };
                    continue;
                }

                // Create zones based on horizontal position (life stages = seasons)
                // Adjust for border: subtract 2 from cols/rows for playable area
                const playableCols = this.cols - 2;
                const playableRows = this.rows - 2;
                const lifeProgress = (col - 1) / playableCols;

                // Vertical position affects tile variation within season
                const verticalPos = (row - 1) / playableRows;

                let tileType;

                // SPRING (0-25%) - Bloom, greener pastures
                if (lifeProgress < 0.25) {
                    if (verticalPos < 0.33) {
                        tileType = 'spring_grass1';
                    } else if (verticalPos < 0.66) {
                        tileType = 'spring_grass2';
                    } else {
                        tileType = 'spring_meadow';
                    }
                }
                // SUMMER (25-50%) - Most colorful, bright sunshine
                else if (lifeProgress < 0.50) {
                    if (verticalPos < 0.33) {
                        tileType = 'summer_field1';
                    } else if (verticalPos < 0.66) {
                        tileType = 'summer_field2';
                    } else {
                        tileType = 'summer_garden';
                    }
                }
                // AUTUMN (50-75%) - Yellowish leaves
                else if (lifeProgress < 0.75) {
                    if (verticalPos < 0.33) {
                        tileType = 'autumn_leaves1';
                    } else if (verticalPos < 0.66) {
                        tileType = 'autumn_leaves2';
                    } else {
                        tileType = 'autumn_ground';
                    }
                }
                // WINTER (75-100%) - Blues, greys
                else {
                    if (verticalPos < 0.33) {
                        tileType = 'winter_snow1';
                    } else if (verticalPos < 0.66) {
                        tileType = 'winter_snow2';
                    } else {
                        tileType = 'winter_frost';
                    }
                }

                this.tiles[row][col] = {
                    type: tileType,
                    x: col * this.tileSize,
                    y: row * this.tileSize
                };
            }
        }
    }

    draw(ctx, camera) {
        // Calculate which tiles are visible
        const startCol = Math.floor(camera.x / this.tileSize);
        const endCol = Math.min(
            Math.ceil((camera.x + camera.viewWidth) / this.tileSize),
            this.cols
        );
        const startRow = Math.floor(camera.y / this.tileSize);
        const endRow = Math.min(
            Math.ceil((camera.y + camera.viewHeight) / this.tileSize),
            this.rows
        );

        ctx.save();

        // Only draw visible tiles
        for (let row = startRow; row < endRow; row++) {
            for (let col = startCol; col < endCol; col++) {
                if (!this.tiles[row] || !this.tiles[row][col]) continue;

                const tile = this.tiles[row][col];
                const tileInfo = this.tileTypes[tile.type];
                const screenPos = camera.worldToScreen(tile.x, tile.y);

                // Draw tile with base color (rounded rectangle)
                const radius = 3; // Corner radius for rounded edges
                ctx.fillStyle = tileInfo.color;
                ctx.beginPath();
                ctx.moveTo(screenPos.x + radius, screenPos.y);
                ctx.lineTo(screenPos.x + this.tileSize - radius, screenPos.y);
                ctx.quadraticCurveTo(screenPos.x + this.tileSize, screenPos.y, screenPos.x + this.tileSize, screenPos.y + radius);
                ctx.lineTo(screenPos.x + this.tileSize, screenPos.y + this.tileSize - radius);
                ctx.quadraticCurveTo(screenPos.x + this.tileSize, screenPos.y + this.tileSize, screenPos.x + this.tileSize - radius, screenPos.y + this.tileSize);
                ctx.lineTo(screenPos.x + radius, screenPos.y + this.tileSize);
                ctx.quadraticCurveTo(screenPos.x, screenPos.y + this.tileSize, screenPos.x, screenPos.y + this.tileSize - radius);
                ctx.lineTo(screenPos.x, screenPos.y + radius);
                ctx.quadraticCurveTo(screenPos.x, screenPos.y, screenPos.x + radius, screenPos.y);
                ctx.closePath();
                ctx.fill();

                // Add texture/patterns (skip for border tiles)
                if (tile.type !== 'border') {
                    this.addTexture(ctx, screenPos.x, screenPos.y, tile.type, tile.x, tile.y);
                }

                // Draw border for all tiles (visible grid with rounded edges)
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(screenPos.x + radius, screenPos.y);
                ctx.lineTo(screenPos.x + this.tileSize - radius, screenPos.y);
                ctx.quadraticCurveTo(screenPos.x + this.tileSize, screenPos.y, screenPos.x + this.tileSize, screenPos.y + radius);
                ctx.lineTo(screenPos.x + this.tileSize, screenPos.y + this.tileSize - radius);
                ctx.quadraticCurveTo(screenPos.x + this.tileSize, screenPos.y + this.tileSize, screenPos.x + this.tileSize - radius, screenPos.y + this.tileSize);
                ctx.lineTo(screenPos.x + radius, screenPos.y + this.tileSize);
                ctx.quadraticCurveTo(screenPos.x, screenPos.y + this.tileSize, screenPos.x, screenPos.y + this.tileSize - radius);
                ctx.lineTo(screenPos.x, screenPos.y + radius);
                ctx.quadraticCurveTo(screenPos.x, screenPos.y, screenPos.x + radius, screenPos.y);
                ctx.closePath();
                ctx.stroke();
            }
        }

        ctx.restore();
    }

    addTexture(ctx, screenX, screenY, type, worldX, worldY) {
        // Add seasonal patterns and decorative elements
        ctx.save();

        const centerX = screenX + this.tileSize / 2;
        const centerY = screenY + this.tileSize / 2;
        // Use world coordinates for seed so particles stay in place
        const seed = (Math.floor(worldX) + Math.floor(worldY) * 1000) % 100; // Pseudo-random seed based on world position

        // SPRING - Bloom, flowers, fresh growth
        if (type.startsWith('spring_')) {
            // Add flower-like patterns
            ctx.fillStyle = 'rgba(255, 192, 203, 0.4)'; // Pink flowers
            for (let i = 0; i < 2; i++) {
                const flowerX = screenX + (seed + i * 30) % this.tileSize;
                const flowerY = screenY + (seed * 1.3 + i * 25) % this.tileSize;
                // Draw simple flower shape (circle with petals)
                ctx.beginPath();
                ctx.arc(flowerX, flowerY, 2, 0, Math.PI * 2);
                ctx.fill();
                // Petals
                ctx.fillStyle = 'rgba(255, 182, 193, 0.3)';
                for (let p = 0; p < 4; p++) {
                    const angle = (p * Math.PI * 2) / 4;
                    ctx.beginPath();
                    ctx.arc(flowerX + Math.cos(angle) * 3, flowerY + Math.sin(angle) * 3, 1.5, 0, Math.PI * 2);
                    ctx.fill();
                }
                ctx.fillStyle = 'rgba(255, 192, 203, 0.4)';
            }
            // Add small grass blades
            ctx.fillStyle = 'rgba(76, 175, 80, 0.2)';
            for (let i = 0; i < 3; i++) {
                const bladeX = screenX + (seed + i * 20) % this.tileSize;
                const bladeY = screenY + this.tileSize - 2;
                ctx.fillRect(bladeX, bladeY, 1, 3);
            }
        }
        // SUMMER - Bright, colorful, sunshine patterns
        else if (type.startsWith('summer_')) {
            // Add bright sun rays/patterns
            ctx.fillStyle = 'rgba(255, 255, 200, 0.3)'; // Bright yellow overlay
            ctx.fillRect(screenX, screenY, this.tileSize, this.tileSize);
            
            // Add colorful flower-like elements
            const colors = ['rgba(255, 87, 34, 0.4)', 'rgba(255, 152, 0, 0.4)', 'rgba(255, 235, 59, 0.4)'];
            for (let i = 0; i < 2; i++) {
                ctx.fillStyle = colors[(seed + i) % colors.length];
                const flowerX = screenX + (seed + i * 35) % this.tileSize;
                const flowerY = screenY + (seed * 1.5 + i * 30) % this.tileSize;
                // Bright circular flowers
                ctx.beginPath();
                ctx.arc(flowerX, flowerY, 3, 0, Math.PI * 2);
                ctx.fill();
            }
            // Add sunshine rays (diagonal lines)
            ctx.strokeStyle = 'rgba(255, 235, 59, 0.2)';
            ctx.lineWidth = 1;
            for (let i = 0; i < 2; i++) {
                ctx.beginPath();
                const rayX = screenX + (seed + i * 40) % this.tileSize;
                ctx.moveTo(rayX, screenY);
                ctx.lineTo(rayX + 5, screenY + 5);
                ctx.stroke();
            }
        }
        // AUTUMN - Yellowish leaves, fall patterns
        else if (type.startsWith('autumn_')) {
            // Add leaf-like patterns
            const leafColors = ['rgba(255, 152, 0, 0.5)', 'rgba(255, 193, 7, 0.5)', 'rgba(244, 67, 54, 0.4)'];
            for (let i = 0; i < 3; i++) {
                ctx.fillStyle = leafColors[(seed + i) % leafColors.length];
                const leafX = screenX + (seed + i * 25) % this.tileSize;
                const leafY = screenY + (seed * 1.2 + i * 20) % this.tileSize;
                // Draw simple leaf shape (oval)
                ctx.beginPath();
                ctx.ellipse(leafX, leafY, 2, 3, (seed + i) * 0.1, 0, Math.PI * 2);
                ctx.fill();
            }
            // Add ground texture (small dots)
            ctx.fillStyle = 'rgba(141, 110, 99, 0.2)';
            for (let i = 0; i < 4; i++) {
                const dotX = screenX + (seed + i * 18) % this.tileSize;
                const dotY = screenY + (seed * 0.8 + i * 15) % this.tileSize;
                ctx.beginPath();
                ctx.arc(dotX, dotY, 1, 0, Math.PI * 2);
                ctx.fill();
            }
        }
        // WINTER - Blues, greys, frost patterns
        else if (type.startsWith('winter_')) {
            // Add frost/snowflake patterns
            ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
            for (let i = 0; i < 2; i++) {
                const flakeX = screenX + (seed + i * 40) % this.tileSize;
                const flakeY = screenY + (seed * 1.1 + i * 35) % this.tileSize;
                // Simple snowflake (cross pattern)
                ctx.fillRect(flakeX - 1, flakeY, 3, 1);
                ctx.fillRect(flakeX, flakeY - 1, 1, 3);
                // Diagonal lines
                ctx.save();
                ctx.translate(flakeX, flakeY);
                ctx.rotate(Math.PI / 4);
                ctx.fillRect(-1, -0.5, 2, 1);
                ctx.restore();
            }
            // Add subtle blue tint overlay
            ctx.fillStyle = 'rgba(176, 190, 197, 0.15)';
            ctx.fillRect(screenX, screenY, this.tileSize, this.tileSize);
            // Add small ice crystals
            ctx.fillStyle = 'rgba(207, 216, 220, 0.3)';
            for (let i = 0; i < 2; i++) {
                const crystalX = screenX + (seed + i * 30) % this.tileSize;
                const crystalY = screenY + (seed * 0.9 + i * 28) % this.tileSize;
                ctx.beginPath();
                ctx.arc(crystalX, crystalY, 1.5, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();
    }
}

// Person class for trail followers (parents, partner, children)
class Person {
    constructor(type, color, targetX, targetY, name = null, namePool = null) {
        this.type = type; // 'parent', 'partner', 'child'
        this.color = color;
        this.x = targetX;
        this.y = targetY;
        this.size = 22;
        this.targetX = targetX;
        this.targetY = targetY;

        // Animation
        this.animOffset = Math.random() * Math.PI * 2; // Random phase offset for variety
        this.lastX = targetX;
        this.lastY = targetY;

        // Set name based on type
        if (name) {
            this.name = name;
        } else if (type === 'partner') {
            // Show unjoined partner label until joined, then use partner name from data
            this.name = GAME_DATA.labels.person.partnerUnjoined;
        } else if (type === 'child') {
            // Random child name from provided pool
            if (namePool && namePool.length > 0) {
                this.name = namePool[Math.floor(Math.random() * namePool.length)];
            } else {
                // Fallback if no pool provided
                this.name = GAME_DATA.names.childFallback[Math.floor(Math.random() * GAME_DATA.names.childFallback.length)];
            }
        } else if (type === 'parent') {
            // Will be set as Mom or Dad when initialized in game
            this.name = 'Parent';
        } else {
            this.name = type.charAt(0).toUpperCase() + type.slice(1);
        }
    }

    update(targetX, targetY) {
        // Store last position for movement detection
        this.lastX = this.x;
        this.lastY = this.y;

        // Smoothly follow target position
        this.targetX = targetX;
        this.targetY = targetY;

        this.x = lerp(this.x, this.targetX, CONFIG.TRAIL_FOLLOW_SPEED);
        this.y = lerp(this.y, this.targetY, CONFIG.TRAIL_FOLLOW_SPEED);
    }

    draw(ctx, camera) {
        const screenPos = camera.worldToScreen(this.x, this.y);
        const time = Date.now() / 1000;

        // Check if moving
        const dx = this.x - this.lastX;
        const dy = this.y - this.lastY;
        const isMoving = Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1;

        // Bobbing animation when moving
        let bobOffset = 0;
        let squish = 1;
        if (isMoving) {
            bobOffset = Math.sin(time * 8 + this.animOffset) * 3;
            squish = 1 + Math.sin(time * 8 + this.animOffset) * 0.08;
        }

        // Gentle breathing animation when still
        const breathe = 1 + Math.sin(time * 2 + this.animOffset) * 0.03;

        ctx.save();

        // Apply transforms
        ctx.translate(screenPos.x, screenPos.y + bobOffset);
        ctx.scale(isMoving ? squish : breathe, isMoving ? (2 - squish) : breathe);

        ctx.fillStyle = this.color;

        // Draw as circle
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.fill();

        // White outline (like other elements)
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.stroke();

        // Reset scale for text
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.translate(screenPos.x, screenPos.y + bobOffset);

        // Draw name label
        let label = this.name;
        // If partner is joined, show partner name from data instead of unjoined label
        if (this.type === 'partner' && this.joined) {
            label = GAME_DATA.names.partner;
        }
        ctx.fillStyle = '#000'; // Black
        ctx.font = '11px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, 0, 0);

        ctx.restore();
    }
}

// Memory class for deceased loved ones
class Memory {
    constructor(type, name = null) {
        this.type = type; // 'parent1', 'parent2', 'partner'
        this.processed = false;
        this.lastVisited = Date.now();
        this.x = 0;
        this.y = 0;
        this.size = 20;
        this.targetX = 0;
        this.targetY = 0;

        // Set name based on type
        if (name) {
            this.name = name;
        } else if (type === 'parent1') {
            this.name = GAME_DATA.names.parent1;
        } else if (type === 'parent2') {
            this.name = GAME_DATA.names.parent2;
        } else if (type === 'partner') {
            this.name = GAME_DATA.names.partner;
        }

        // Set grief penalties based on type
        if (type.includes('parent')) {
            this.unprocessedPenalty = CONFIG.UNPROCESSED_PARENT_GRIEF;
            this.processedPenalty = CONFIG.PROCESSED_PARENT_GRIEF;
        } else if (type === 'partner') {
            this.unprocessedPenalty = CONFIG.UNPROCESSED_PARTNER_GRIEF;
            this.processedPenalty = CONFIG.PROCESSED_PARTNER_GRIEF;
        }

        this.currentGriefPenalty = this.unprocessedPenalty;
    }

    visit() {
        this.processed = true;
        this.lastVisited = Date.now();
        this.currentGriefPenalty = this.processedPenalty;
    }

    update(targetX, targetY, deltaTime) {
        this.targetX = targetX;
        this.targetY = targetY;

        // Float around the target position
        const time = Date.now() / 1000;
        const offset = Math.sin(time * 2) * 5;

        this.x = lerp(this.x, this.targetX + offset, CONFIG.TRAIL_FOLLOW_SPEED);
        this.y = lerp(this.y, this.targetY + offset, CONFIG.TRAIL_FOLLOW_SPEED);
    }

    shouldPulse() {
        // Pulse if not yet processed
        return !this.processed;
    }

    draw(ctx, camera) {
        const screenPos = camera.worldToScreen(this.x, this.y);

        ctx.save();

        // Choose color based on processed state
        let orbColor, textColor, outerColor;
        if (this.processed) {
            orbColor = 'rgba(255,255,255,0.35)'; // more transparent white
            textColor = '#333';
            outerColor = 'rgba(255,255,255,0.18)';
        } else {
            orbColor = '#111'; // black/dark
            textColor = '#fff';
            outerColor = 'rgba(0,0,0,0.5)';
        }

        // Pulse if needed
        let size = this.size;
        if (this.shouldPulse()) {
            const pulse = Math.sin(Date.now() / 200) * 2;
            size += pulse;
        }

        // Draw as glowing orb
        const gradient = ctx.createRadialGradient(
            screenPos.x, screenPos.y, 0,
            screenPos.x, screenPos.y, size
        );
        gradient.addColorStop(0, orbColor);
        gradient.addColorStop(1, outerColor);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, size, 0, Math.PI * 2);
        ctx.fill();

        // Draw text label
        ctx.fillStyle = textColor;
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.name, screenPos.x, screenPos.y);

        ctx.restore();
    }
}

// Grave class for deceased loved ones
class Grave {
    constructor(memory, x, y) {
        this.memory = memory; // Reference to the associated memory
        this.x = x; // Position where the person died
        this.y = y;
        this.size = 25;
        this.processed = false; // Whether the death grief has been processed (first visit)

        // Memory display
        this.currentMemoryString = null; // The memory being shown
        this.memoryDisplayTime = 0; // Time remaining to show memory
        this.memoryDisplayDuration = 3.5; // Show memory for 3.5 seconds after visiting

        // Animation
        this.animOffset = Math.random() * Math.PI * 2;
        this.hoverAmount = 0; // For proximity scaling effect
        this.pulsePhase = Math.random() * Math.PI * 2; // For pulsing animation
        this.floatPhase = Math.random() * Math.PI * 2; // For floating animation
    }

    // Can always interact with graves (for revisits)
    canInteract(playerX, playerY) {
        const dist = distance(this.x, this.y, playerX, playerY);
        return dist < CONFIG.EXPERIENCE_INTERACT_DISTANCE;
    }

    isNearby(playerX, playerY) {
        const dist = distance(this.x, this.y, playerX, playerY);
        return dist < CONFIG.EXPERIENCE_INTERACT_DISTANCE * 1.5;
    }

    // Update proximity hover effect (like experiences)
    updateProximity(playerX, playerY) {
        const dist = distance(this.x, this.y, playerX, playerY);
        const maxDist = CONFIG.EXPERIENCE_INTERACT_DISTANCE * 2.5;
        if (dist < maxDist) {
            const targetHover = 1 - (dist / maxDist);
            this.hoverAmount = lerp(this.hoverAmount, targetHover, 0.15);
        } else {
            this.hoverAmount = lerp(this.hoverAmount, 0, 0.1);
        }
    }

    // Complete the visit and show memory (called when interaction completes)
    completeVisit() {
        
        // Only process grief on first visit
        if (!this.processed) {
            this.processed = true;
            this.memory.visit(); // Process the associated memory (reduces grief burden)
        }

        // Always show a random memory (even on revisits)
        const memoryType = this.memory.type;
        let memoryStrings = [];
        if (memoryType === 'parent1' && GAME_DATA.memories.parent1) {
            memoryStrings = GAME_DATA.memories.parent1;
        } else if (memoryType === 'parent2' && GAME_DATA.memories.parent2) {
            memoryStrings = GAME_DATA.memories.parent2;
        } else if (memoryType === 'partner' && GAME_DATA.memories.partner) {
            memoryStrings = GAME_DATA.memories.partner;
        }

        if (memoryStrings.length > 0) {
            this.currentMemoryString = memoryStrings[Math.floor(Math.random() * memoryStrings.length)];
            this.memoryDisplayTime = this.memoryDisplayDuration;
        }
    }

    // Update animation and memory display
    update(deltaTime) {
        // Update memory display timer
        if (this.memoryDisplayTime > 0) {
            this.memoryDisplayTime -= deltaTime;
            if (this.memoryDisplayTime <= 0) {
                this.currentMemoryString = null;
            }
        }

        // Update animation phases for unprocessed graves
        if (!this.processed) {
            this.pulsePhase += deltaTime * 3; // Faster pulsing
            this.floatPhase += deltaTime * 1.5; // Gentle floating
        }
    }

    draw(ctx, camera) {
        const screenPos = camera.worldToScreen(this.x, this.y);
        const time = Date.now() / 1000;

        ctx.save();

        // Colors: unprocessed = darker gray, processed = medium gray (darker than before)
        const stoneColor = this.processed ? '#999' : '#555'; // Darker colors for visibility
        const strokeColor = this.processed ? '#777' : '#333';
        const crossColor = this.processed ? '#666' : '#222'; // Darker cross for better visibility

        // Enhanced animations for unprocessed graves
        let pulseScale = 1;
        let floatY = 0;
        let glowIntensity = 0;
        
        if (!this.processed) {
            // More prominent pulsing for unprocessed graves
            pulseScale = 1 + Math.sin(this.pulsePhase) * 0.12; // 12% pulse (was 3%)
            // Floating/bobbing animation
            floatY = Math.sin(this.floatPhase) * 4; // Float up and down
            // Glow intensity pulses with the grave
            glowIntensity = 0.4 + Math.sin(this.pulsePhase) * 0.3; // Pulsing glow
        }

        // Proximity scaling (subtle, not as dramatic as experiences)
        const hoverScale = 1 + this.hoverAmount * 0.15; // 15% max scale increase
        const basePulse = 1 + Math.sin(time * 3 + this.animOffset) * 0.03; // Gentle base pulse
        const finalScale = hoverScale * basePulse * pulseScale;

        ctx.translate(screenPos.x, screenPos.y + floatY);
        ctx.scale(finalScale, finalScale);
        ctx.translate(-screenPos.x, -(screenPos.y + floatY));

        // Enhanced glow effect for unprocessed graves (priority over memory glow)
        if (!this.processed) {
            // Pulsing glow around unprocessed grave - more prominent
            ctx.shadowColor = 'rgba(150, 150, 200, 0.9)';
            ctx.shadowBlur = 18 + Math.sin(this.pulsePhase) * 12; // Stronger pulsing shadow blur
        } else if (this.memoryDisplayTime > 0) {
            // Cool green glow effect while showing memory (processed graves)
            const glowAmount = this.memoryDisplayTime / this.memoryDisplayDuration;
            ctx.shadowColor = '#4ECDC4'; // Cool teal-green
            ctx.shadowBlur = 20 * glowAmount;
        } else if (this.hoverAmount > 0.1) {
            // Normal glow when nearby (processed, no memory)
            ctx.shadowColor = 'rgba(150, 150, 150, 0.5)';
            ctx.shadowBlur = 12 * this.hoverAmount;
        }

        ctx.strokeStyle = strokeColor;
        ctx.fillStyle = stoneColor;
        ctx.lineWidth = 2 / finalScale; // Adjust line width for scale

        // Draw tombstone shape (rounded rectangle)
        const width = this.size * 1.2;
        const height = this.size * 1.5;
        const cornerRadius = 4;

        ctx.beginPath();
        ctx.moveTo(screenPos.x - width / 2 + cornerRadius, screenPos.y - height / 2);
        ctx.lineTo(screenPos.x + width / 2 - cornerRadius, screenPos.y - height / 2);
        ctx.quadraticCurveTo(screenPos.x + width / 2, screenPos.y - height / 2, screenPos.x + width / 2, screenPos.y - height / 2 + cornerRadius);
        ctx.lineTo(screenPos.x + width / 2, screenPos.y + height / 2 - cornerRadius);
        ctx.quadraticCurveTo(screenPos.x + width / 2, screenPos.y + height / 2, screenPos.x + width / 2 - cornerRadius, screenPos.y + height / 2);
        ctx.lineTo(screenPos.x - width / 2 + cornerRadius, screenPos.y + height / 2);
        ctx.quadraticCurveTo(screenPos.x - width / 2, screenPos.y + height / 2, screenPos.x - width / 2, screenPos.y + height / 2 - cornerRadius);
        ctx.lineTo(screenPos.x - width / 2, screenPos.y - height / 2 + cornerRadius);
        ctx.quadraticCurveTo(screenPos.x - width / 2, screenPos.y - height / 2, screenPos.x - width / 2 + cornerRadius, screenPos.y - height / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        ctx.shadowBlur = 0;

        // Always draw a simple cross on top (color changes based on processed state)
        ctx.strokeStyle = crossColor;
        ctx.lineWidth = 3 / finalScale; // Adjust for scale
        
        // For unprocessed graves, add a subtle shimmer effect to the cross
        if (!this.processed) {
            const shimmer = Math.sin(this.pulsePhase * 2) * 0.3;
            ctx.globalAlpha = 0.7 + shimmer; // Subtle alpha pulse
        }
        
        ctx.beginPath();
        // Vertical line
        ctx.moveTo(screenPos.x, screenPos.y - height / 2 + 5);
        ctx.lineTo(screenPos.x, screenPos.y - height / 2 + 15);
        // Horizontal line
        ctx.moveTo(screenPos.x - 5, screenPos.y - height / 2 + 10);
        ctx.lineTo(screenPos.x + 5, screenPos.y - height / 2 + 10);
        ctx.stroke();
        
        ctx.globalAlpha = 1; // Reset alpha

        // Reset transform for progress indicator (needs to be drawn at original scale)
        ctx.restore();
        ctx.save();

        ctx.restore();
    }

    // Draw memory text (called separately so it renders on top)
    drawMemory(ctx, camera) {
        if (!this.currentMemoryString || this.memoryDisplayTime <= 0) return;

        const screenPos = camera.worldToScreen(this.x, this.y);
        const alpha = Math.min(1, this.memoryDisplayTime / 0.5); // Fade out in last 0.5s
        const fadeInAlpha = Math.min(1, (this.memoryDisplayDuration - this.memoryDisplayTime) / 0.3); // Fade in
        const finalAlpha = Math.min(alpha, fadeInAlpha);

        ctx.save();
        ctx.globalAlpha = finalAlpha;

        // Memory text box
        const titleText = 'memory';
        const memoryText = `"${this.currentMemoryString}"`;
        
        // Measure both texts
        ctx.font = 'bold 14px Georgia';
        const titleWidth = ctx.measureText(titleText).width;
        ctx.font = 'italic 16px Georgia';
        const memoryWidth = ctx.measureText(memoryText).width;
        
        // Box width is the wider of the two texts plus padding
        const boxWidth = Math.max(titleWidth, memoryWidth) + 30;
        const boxHeight = 60; // Increased to accommodate title and memory text
        const boxX = screenPos.x - boxWidth / 2;
        const boxY = screenPos.y - this.size * 2 - 50;

        // Soft cool green background
        ctx.fillStyle = 'rgba(230, 248, 255, 0.95)'; // Cool blue-green tint
        ctx.shadowColor = 'rgba(78, 205, 196, 0.4)'; // Teal-green shadow
        ctx.shadowBlur = 15;
        
        // Rounded box
        const radius = 8;
        ctx.beginPath();
        ctx.moveTo(boxX + radius, boxY);
        ctx.lineTo(boxX + boxWidth - radius, boxY);
        ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
        ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
        ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - radius, boxY + boxHeight);
        ctx.lineTo(boxX + radius, boxY + boxHeight);
        ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - radius);
        ctx.lineTo(boxX, boxY + radius);
        ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
        ctx.closePath();
        ctx.fill();

        ctx.shadowBlur = 0;

        // Border
        ctx.strokeStyle = 'rgba(200, 170, 100, 0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Title text (centered, above)
        ctx.fillStyle = '#5c4033';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = 'bold 14px Georgia';
        ctx.fillText(titleText, screenPos.x, boxY + 18);

        // Memory text (centered, below title)
        ctx.font = 'italic 16px Georgia';
        ctx.fillText(memoryText, screenPos.x, boxY + 42);

        ctx.restore();
    }
}

// Experience class for collectible items

// Color pool for experiences - 15 varied, pleasant colors
const EXPERIENCE_COLORS = [
    '#FF6B8A',  // Rose pink
    '#FF8C5A',  // Coral orange
    '#FFB347',  // Pastel orange
    '#F7DC6F',  // Soft yellow
    '#82E0AA',  // Mint green
    '#58D68D',  // Spring green
    '#5DADE2',  // Sky blue
    '#85C1E9',  // Light blue
    '#AF7AC5',  // Soft purple
    '#D7BDE2',  // Lavender
    '#F1948A',  // Salmon
    '#73C6B6',  // Teal
    '#F8B500',  // Golden
    '#E74C3C',  // Vibrant red
    '#3498DB',  // Ocean blue
];

class Experience {
    constructor(type, x, y) {
        this.type = type; // 'nature', 'knowledge', 'pleasure'
        this.x = x;
        this.y = y;
        this.size = 20;
        this.collected = false;
        this.specificName = null; // Will be set on collection

        // Animation
        this.animOffset = Math.random() * Math.PI * 2;
        this.hoverAmount = 0; // For proximity hover effect

        // Assign random color from pool
        this.color = EXPERIENCE_COLORS[Math.floor(Math.random() * EXPERIENCE_COLORS.length)];

        // Set data based on type
        const experienceData = GAME_DATA.experiences[type];
        if (experienceData) {
            this.possibleNames = experienceData.possibleNames;
        }
    }

    collect() {
        // Pick a random specific name when collected
        const randomIndex = Math.floor(Math.random() * this.possibleNames.length);
        this.specificName = this.possibleNames[randomIndex];
        this.collected = true;
        return this.specificName;
    }

    canInteract(playerX, playerY) {
        if (this.collected) return false;
        const dist = distance(this.x, this.y, playerX, playerY);
        return dist < CONFIG.EXPERIENCE_INTERACT_DISTANCE;
    }

    // Update hover amount based on player proximity
    updateProximity(playerX, playerY) {
        if (this.collected) return;
        const dist = distance(this.x, this.y, playerX, playerY);
        const maxDist = CONFIG.EXPERIENCE_INTERACT_DISTANCE * 2.5;
        if (dist < maxDist) {
            const targetHover = 1 - (dist / maxDist);
            this.hoverAmount = lerp(this.hoverAmount, targetHover, 0.15);
        } else {
            this.hoverAmount = lerp(this.hoverAmount, 0, 0.1);
        }
    }

    draw(ctx, camera) {
        if (this.collected) return;

        const screenPos = camera.worldToScreen(this.x, this.y);
        const time = Date.now() / 1000;

        ctx.save();

        // Floating animation
        const floatY = Math.sin(time * 2 + this.animOffset) * 4;
        const floatX = Math.cos(time * 1.5 + this.animOffset) * 2;

        // Scale based on hover (proximity)
        const hoverScale = 1 + this.hoverAmount * 0.35;
        const pulse = 1 + Math.sin(time * 4 + this.animOffset) * 0.05;
        const finalScale = hoverScale * pulse;

        // Glow effect when close
        if (this.hoverAmount > 0.1) {
            ctx.shadowColor = this.color;
            ctx.shadowBlur = 15 * this.hoverAmount;
        }

        ctx.translate(screenPos.x + floatX, screenPos.y + floatY);
        ctx.scale(finalScale, finalScale);

        // Draw as triangle for nature, square for knowledge, pentagon for pleasure
        ctx.fillStyle = this.color;
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2 / finalScale; // Keep line width consistent

        ctx.beginPath();
        if (this.type === 'nature') {
            // Triangle
            ctx.moveTo(0, -this.size);
            ctx.lineTo(-this.size, this.size);
            ctx.lineTo(this.size, this.size);
            ctx.closePath();
        } else if (this.type === 'knowledge') {
            // Square
            ctx.rect(-this.size, -this.size, this.size * 2, this.size * 2);
        } else {
            // Pentagon
            const sides = 5;
            const angleStep = (Math.PI * 2) / sides;
            // Start from top (rotate by -90 degrees so top vertex is at top)
            const startAngle = -Math.PI / 2;
            for (let i = 0; i < sides; i++) {
                const angle = startAngle + i * angleStep;
                const px = Math.cos(angle) * this.size;
                const py = Math.sin(angle) * this.size;
                if (i === 0) {
                    ctx.moveTo(px, py);
                } else {
                    ctx.lineTo(px, py);
                }
            }
            ctx.closePath();
        }

        ctx.fill();
        ctx.stroke();

        ctx.restore();
    }
}

// Encounter class for meeting partner and children
class Encounter {
    constructor(type, x, y) {
        this.type = type; // 'partner', 'child1', 'child2'
        this.x = x;
        this.y = y;
        this.size = 25; // Increased base size
        this.triggered = false;
        this.accepted = false;

        // Enhanced animation
        this.hoverAmount = 0;
        this.animOffset = Math.random() * Math.PI * 2;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.rotation = 0;

        // Set color and label based on type
        if (type === 'partner') {
            this.color = CONFIG.COLOR_PARTNER;
            this.label = GAME_DATA.names.partner;
            this.glowColor = '#7DCEA0'; // Brighter version for glow
        } else {
            this.color = CONFIG.COLOR_CHILD;
            this.label = GAME_DATA.labels.encounter.childLabel;
            this.glowColor = '#F9D56E'; // Brighter version for glow
        }
    }

    canInteract(playerX, playerY) {
        if (this.triggered || this.accepted) return false;
        const dist = distance(this.x, this.y, playerX, playerY);
        return dist < 50;
    }

    accept() {
        this.triggered = true;
        this.accepted = true;
    }

    // Update hover amount based on player proximity
    updateProximity(playerX, playerY) {
        if (this.triggered) return;
        const dist = distance(this.x, this.y, playerX, playerY);
        const maxDist = 150; // Increased detection range
        if (dist < maxDist) {
            const targetHover = Math.pow(1 - (dist / maxDist), 0.7); // More dramatic curve
            this.hoverAmount = lerp(this.hoverAmount, targetHover, 0.15); // Faster response
        } else {
            this.hoverAmount = lerp(this.hoverAmount, 0, 0.05); // Slower fade out
        }

        // Update rotation for subtle spinning effect when close (toned down)
        if (this.hoverAmount > 0.1) {
            this.rotation += (0.005 + this.hoverAmount * 0.01); // Reduced rotation speed
        }
    }

    draw(ctx, camera) {
        if (this.triggered) return;

        const screenPos = camera.worldToScreen(this.x, this.y);
        const time = Date.now() / 1000;

        ctx.save();

        // Enhanced floating and pulsing animation
        const floatY = Math.sin(time * 1.8 + this.animOffset) * 8;
        const floatX = Math.cos(time * 1.2 + this.animOffset) * 3;

        // Subtle scaling based on proximity (toned down)
        const basePulse = Math.sin(time * 4 + this.pulsePhase) * 2; // Reduced pulse
        const hoverScale = 1 + Math.pow(this.hoverAmount, 0.8) * 0.3; // Reduced scaling (was 0.8)
        const size = (this.size + basePulse) * hoverScale;

        // Save context before applying rotation (for visual elements only)
        ctx.save();

        // Apply subtle rotation when close (toned down - only when very close)
        const applyRotation = this.hoverAmount > 0.4; // Only rotate when very close
        if (applyRotation) {
            ctx.translate(screenPos.x + floatX, screenPos.y + floatY);
            ctx.rotate(this.rotation * 0.5); // Reduce rotation amount by 50%
            ctx.translate(-(screenPos.x + floatX), -(screenPos.y + floatY));
        }

        // Multi-layer glow effect
        const glowIntensity = this.hoverAmount * 0.8;
        if (glowIntensity > 0.05) {
            // Outer glow
            ctx.shadowColor = this.glowColor;
            ctx.shadowBlur = 30 * glowIntensity;
            ctx.globalAlpha = 0.3 * glowIntensity;

            ctx.beginPath();
            ctx.arc(screenPos.x + floatX, screenPos.y + floatY, size + 15, 0, Math.PI * 2);
            ctx.fill();

            // Inner glow
            ctx.shadowBlur = 15 * glowIntensity;
            ctx.globalAlpha = 0.5 * glowIntensity;

            ctx.beginPath();
            ctx.arc(screenPos.x + floatX, screenPos.y + floatY, size + 8, 0, Math.PI * 2);
            ctx.fill();
        }

        // Main encounter shape with enhanced effects
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 12 + 20 * this.hoverAmount;
        ctx.fillStyle = this.color;
        ctx.strokeStyle = this.glowColor;
        ctx.lineWidth = 3 + this.hoverAmount * 2;
        ctx.globalAlpha = 0.8 + this.hoverAmount * 0.4;

        // Draw multiple overlapping shapes for more presence (reduced layers)
        const layers = 1 + Math.floor(this.hoverAmount * 2); // Reduced from 3 to 2
        for (let i = 0; i < layers; i++) {
            const layerSize = size * (1 - i * 0.08); // Reduced layer size difference
            const layerAlpha = ctx.globalAlpha * (1 - i * 0.25);

            ctx.globalAlpha = layerAlpha;
            ctx.beginPath();
            ctx.arc(screenPos.x + floatX, screenPos.y + floatY, layerSize, 0, Math.PI * 2);
            ctx.fill();
        }
        
        // Add visible outline (like experiences and graves)
        ctx.save();
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#fff'; // White outline for visibility
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.arc(screenPos.x + floatX, screenPos.y + floatY, size, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();

        // Energy rings when close
        if (this.hoverAmount > 0.3) {
            ctx.strokeStyle = this.glowColor;
            ctx.lineWidth = 2;
            ctx.globalAlpha = this.hoverAmount * 0.6;

            // Multiple expanding rings
            for (let i = 0; i < 3; i++) {
                const ringSize = size + 12 + i * 8 + Math.sin(time * 3 + i * 2) * 4;
                ctx.beginPath();
                ctx.arc(screenPos.x + floatX, screenPos.y + floatY, ringSize, 0, Math.PI * 2);
                ctx.stroke();
            }

            // Add a brighter inner ring outline
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 1;
            ctx.globalAlpha = this.hoverAmount * 0.4;
            ctx.beginPath();
            ctx.arc(screenPos.x + floatX, screenPos.y + floatY, size + 8, 0, Math.PI * 2);
            ctx.stroke();

        }

        // Sparkle effects when very close
        if (this.hoverAmount > 0.5) {
            ctx.globalAlpha = this.hoverAmount * 0.8;
            ctx.fillStyle = '#FFFFFF';

            // Draw sparkles around the encounter
            for (let i = 0; i < 5; i++) {
                const angle = (time * 2 + i * Math.PI * 2 / 5) % (Math.PI * 2);
                const sparkleDist = size + 20 + Math.sin(time * 4 + i) * 5;
                const sx = screenPos.x + floatX + Math.cos(angle) * sparkleDist;
                const sy = screenPos.y + floatY + Math.sin(angle) * sparkleDist;
                const sparkleSize = 2 + Math.sin(time * 6 + i) * 1;

                ctx.beginPath();
                ctx.arc(sx, sy, sparkleSize, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Restore context to remove rotation before drawing text
        ctx.restore();

        // Draw label inside circle (same style as parent labels)
        ctx.save();
        ctx.translate(screenPos.x + floatX, screenPos.y + floatY);
        
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#333';
        
        // Use same font and size as parent labels
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.label, 0, 0);

        ctx.restore();
    }
}

// Player class - Jane
class Player {
    constructor(x, y) {
           this.x = x;
           this.y = y;
           this.size = CONFIG.PLAYER_SIZE;

           // Aging system
           // Actual chronological age - incremented by the actual aging rate (accounting for all modifiers)
           this.age = CONFIG.STARTING_AGE; // Current actual age in years
           this.remainingLife = CONFIG.BASE_LIFESPAN; // Years remaining
           this.agingRateModifier = 0; // Modifier to aging rate (positive = slower aging)

           // Movement tracking for blur
           this.movingTime = 0;
           this.stillTime = 0;
           this.blurLevel = 0; // 0-3

           // Aging modifiers from experiences
           this.experienceAgingBonus = 0;

           // Collections
           this.livingPeople = [];
           this.memories = [];
           this.experiences = [];

           // Stats
           this.memoryVisitCount = 0;

           // For collecting/interacting (unified system)
           this.isCollecting = false;
           this.collectingExperience = null;
           this.collectingEncounter = null; // For meeting people
           this.collectingGrave = null; // For visiting graves
           this.collectTimer = 0;
           this.collectDuration = 1; // Default 1 second for experiences
           this.lastCollectedExperience = null;
           this.lastCollectedExperienceType = null;
           this.lastCollectedExperienceColor = null;
           this.lastCollectedTime = 0;
           this.justCollectedExperience = false; // Flag for particle spawn
           this.justCompletedEncounter = null; // Track completed encounter
           this.justCompletedGrave = null; // Track completed grave

           // Animation tracking
           this.lastX = x;
           this.lastY = y;
           this.animTime = 0;
           this.trailParticles = [];
           this.trailSpawnTimer = 0;
    }

    update(deltaTime, debugMode = false) {
        // Store last position for movement detection
        this.lastX = this.x;
        this.lastY = this.y;

        // Update animation time
        this.animTime += deltaTime;

        // Update trail particles
        this.updateTrailParticles(deltaTime);

        if (this.isCollecting) {
            this.handleCollecting(deltaTime);
            return; // Don't move while collecting/interacting
        }

        let dx = 0;
        let dy = 0;

        // Check if keyboard keys are currently pressed
        const keyboardPressed = Input.isDown('arrowright') || Input.isDown('d') || 
                                Input.isDown('arrowleft') || Input.isDown('a') ||
                                Input.isDown('arrowdown') || Input.isDown('s') ||
                                Input.isDown('arrowup') || Input.isDown('w');
        
        // Mouse movement (Agario-style): only enabled if inputMode is 'mouse'
        // In keyboard mode, mouse movement is disabled until user clicks
        if (Input.inputMode === 'mouse' && !keyboardPressed) {
            const hasMouseInput = Input.mouseWorldX !== 0 || Input.mouseWorldY !== 0;
            if (hasMouseInput) {
                const targetX = Input.mouseWorldX;
                const targetY = Input.mouseWorldY;
                const diffX = targetX - this.x;
                const diffY = targetY - this.y;
                const distance = Math.sqrt(diffX * diffX + diffY * diffY);
                
                if (distance > 5) { // Only move if mouse is far enough away
                    dx = diffX / distance;
                    dy = diffY / distance;
                }
            }
        }

        // Keyboard movement (WASD/Arrow keys) - always works when pressed
        // If keyboard is pressed, it takes precedence and also switches to keyboard mode
        if (Input.isDown('arrowright') || Input.isDown('d')) dx = 1;
        else if (Input.isDown('arrowleft') || Input.isDown('a')) dx = -1;
        
        if (Input.isDown('arrowdown') || Input.isDown('s')) dy = 1;
        else if (Input.isDown('arrowup') || Input.isDown('w')) dy = -1;

        // Normalize diagonal movement
        if (dx !== 0 && dy !== 0) {
            dx *= 0.707;
            dy *= 0.707;
        }

        // Get current speed based on age
        const currentSpeed = this.getSpeed();

        // Apply movement with age-based speed
        const moveX = dx * currentSpeed;
        const moveY = dy * currentSpeed;

        this.x += moveX;
        this.y += moveY;

        // Clamp to world bounds (accounting for border tiles - 1 tile = 80 pixels)
        const borderSize = 80; // Tile size used in TileMap
        const minX = this.size + borderSize;
        const maxX = CONFIG.WORLD_WIDTH - this.size - borderSize;
        const minY = this.size + borderSize;
        const maxY = CONFIG.WORLD_HEIGHT - this.size - borderSize;
        
        this.x = clamp(this.x, minX, maxX);
        this.y = clamp(this.y, minY, maxY);

        // Age based on movement (any direction)
        const distanceMoved = Math.abs(moveX) + Math.abs(moveY);
        if (distanceMoved > 0) {
            this.updateAging(deltaTime, debugMode);
        }

        // Update blur system
        this.updateBlur(deltaTime, dx !== 0 || dy !== 0);

        // Spawn trail particles when happy and moving (only if no unprocessed grief)
        const hasUnprocessedGrief = this.memories.some(memory => !memory.processed);
        const capacity = 1 + this.getAgingRateModifier();
        if (capacity > 1.0 && distanceMoved > 0 && !hasUnprocessedGrief) {
            this.trailSpawnTimer += deltaTime;
            if (this.trailSpawnTimer > 0.08) {
                this.trailSpawnTimer = 0;
                this.spawnTrailParticle();
            }
        }
    }

    updateTrailParticles(deltaTime) {
        for (let i = this.trailParticles.length - 1; i >= 0; i--) {
            const p = this.trailParticles[i];
            p.life -= deltaTime;
            if (p.life <= 0) {
                this.trailParticles.splice(i, 1);
                continue;
            }
            p.x += p.vx * deltaTime;
            p.y += p.vy * deltaTime;
            p.vy -= 15 * deltaTime; // Float upward
            p.alpha = p.life / p.maxLife;
            p.size *= 0.98;
        }
    }

    spawnTrailParticle() {
        const colors = ['#FFD700', '#FFA500', '#FF69B4', '#98FB98', '#87CEEB'];
        this.trailParticles.push({
            x: this.x + (Math.random() - 0.5) * 10,
            y: this.y + (Math.random() - 0.5) * 10,
            vx: (Math.random() - 0.5) * 20,
            vy: (Math.random() - 0.5) * 20,
            size: 3 + Math.random() * 4,
            color: colors[Math.floor(Math.random() * colors.length)],
            life: 0.5 + Math.random() * 0.4,
            maxLife: 0.5 + Math.random() * 0.4,
            alpha: 1
        });
    }

    getSpeed() {
        // First 40 years: full speed
        if (this.age <= 40) {
            return CONFIG.BASE_SPEED;
        }

        // After 40: linear decrease from full speed to half speed by age 80
        // Formula: speed = BASE_SPEED * (120 - age) / 80
        // At age 40: speed = BASE_SPEED * 80/80 = BASE_SPEED (full)
        // At age 80: speed = BASE_SPEED * 40/80 = BASE_SPEED/2 (half)
        // Beyond 80: continues decreasing proportionally
        const speed = CONFIG.BASE_SPEED * (120 - this.age) / 80;

        // Ensure speed doesn't go below 0
        return Math.max(0, speed);
    }

    updateAging(deltaTime, debugMode = false) {
        // Calculate aging rate modifier from all sources
        const agingRateModifier = this.getAgingRateModifier();

        // Base aging rate: BASE_LIFESPAN years over GAMEPLAY_DURATION seconds
        // So we age (BASE_LIFESPAN / GAMEPLAY_DURATION) years per second
        const baseAgingRate = CONFIG.BASE_LIFESPAN / CONFIG.GAMEPLAY_DURATION;

        // Apply modifiers to how fast "life" depletes
        // Positive modifier = slower life depletion = live longer
        let lifeDepletionRate = baseAgingRate / (1 + agingRateModifier);

        // Calendar age increases at BASE rate (time passes at fixed speed)
        // But remainingLife decreases at MODIFIED rate (better life = slower depletion)
        let calendarAgingRate = baseAgingRate;

        // Update age (calendar years lived)
        const yearsLived = calendarAgingRate * deltaTime;
        this.age += yearsLived;

        // Update remaining life (depletes slower with positive modifiers)
        const lifeDepleted = lifeDepletionRate * deltaTime;
        this.remainingLife -= lifeDepleted;

        // Ensure we don't go below 0
        if (this.remainingLife < 0) {
            this.remainingLife = 0;
        }
    }

    // Get the actual chronological age (this.age already represents the actual age)
    getActualAge() {
        return this.age;
    }

    updateBlur(deltaTime, isMoving) {
        if (isMoving) {
            this.movingTime += deltaTime;
            this.stillTime = 0;

            // Update blur level based on movement time
            if (this.movingTime >= CONFIG.BLUR_THRESHOLD_3) {
                this.blurLevel = 3;
            } else if (this.movingTime >= CONFIG.BLUR_THRESHOLD_2) {
                this.blurLevel = 2;
            } else if (this.movingTime >= CONFIG.BLUR_THRESHOLD_1) {
                this.blurLevel = 1;
            }
        } else {
            this.stillTime += deltaTime;
            this.movingTime = 0;

            // Clear blur after standing still
            if (this.stillTime >= CONFIG.BLUR_CLEAR_TIME) {
                this.blurLevel = 0;
            }
        }
    }

    getAgingRateModifier() {
        let modifier = 0;

        // Family bonuses (positive = slower aging)
        this.livingPeople.forEach(person => {
            if (person.type === 'partner') {
                modifier += CONFIG.PARTNER_AGING_BONUS;
            } else if (person.type === 'child') {
                modifier += CONFIG.CHILD_AGING_BONUS;
            }
        });

        // Grief penalties (negative = faster aging)
        this.memories.forEach(memory => {
            modifier += memory.currentGriefPenalty;
        });

        // Experience bonus (positive = slower aging)
        modifier += this.experienceAgingBonus;

        return modifier;
    }

    startCollecting(experience) {
        this.isCollecting = true;
        this.collectingExperience = experience;
        this.collectingEncounter = null;
        this.collectingGrave = null;
        this.collectTimer = 0;
        this.collectDuration = CONFIG.EXPERIENCE_COLLECT_TIME; // 1 second
        this.blurLevel = 0; // Clear blur when interacting
        this.movingTime = 0;
    }

    startInteracting(encounter) {
        this.isCollecting = true;
        this.collectingExperience = null;
        this.collectingEncounter = encounter;
        this.collectingGrave = null;
        this.collectTimer = 0;
        this.collectDuration = 2; // 2 seconds for meeting people
        this.blurLevel = 0;
        this.movingTime = 0;
    }

    startVisiting(grave) {
        this.isCollecting = true;
        this.collectingExperience = null;
        this.collectingEncounter = null;
        this.collectingGrave = grave;
        this.collectTimer = 0;
        this.collectDuration = 3; // 3 seconds for grave visits
        this.blurLevel = 0;
        this.movingTime = 0;
    }

    handleCollecting(deltaTime) {
        this.collectTimer += deltaTime;

        if (this.collectTimer >= this.collectDuration) {
            // Interaction complete
            if (this.collectingExperience) {
                // Experience collection
                const specificName = this.collectingExperience.collect();
                this.experiences.push(this.collectingExperience);

                this.lastCollectedExperience = specificName;
                this.lastCollectedExperienceType = this.collectingExperience.type;
                this.lastCollectedExperienceColor = this.collectingExperience.color;
                this.lastCollectedTime = Date.now();
                this.justCollectedExperience = true;

                this.experienceAgingBonus = Math.min(
                    this.experienceAgingBonus + CONFIG.EXPERIENCE_AGING_BONUS,
                    CONFIG.MAX_EXPERIENCE_BONUS
                );

                this.collectingExperience = null;
            } else if (this.collectingEncounter) {
                // Encounter - signal completion to game
                this.justCompletedEncounter = this.collectingEncounter;
                this.collectingEncounter = null;
            } else if (this.collectingGrave) {
                // Grave visit - signal completion to game
                this.justCompletedGrave = this.collectingGrave;
                this.collectingGrave = null;
            }

            // Reset collecting state
            this.isCollecting = false;
            this.collectTimer = 0;
        }
    }

    addPerson(person) {
        this.livingPeople.push(person);
    }

    addMemory(memory) {
        this.memories.push(memory);
    }

    visitMemory(memory) {
        memory.visit();
        this.memoryVisitCount++;
        this.blurLevel = 0; // Clear blur when visiting memory
    }

    draw(ctx, camera) {
        const screenPos = camera.worldToScreen(this.x, this.y);
        const time = this.animTime;

        // Check if moving
        const dx = this.x - this.lastX;
        const dy = this.y - this.lastY;
        const isMoving = Math.abs(dx) > 0.05 || Math.abs(dy) > 0.05;

        // Get brightness based on experience count
        const brightness = 1 + (this.experiences.length * 0.05);

        // Check if there's unprocessed grief (any memory not processed)
        const hasUnprocessedGrief = this.memories.some(memory => !memory.processed);

        // Determine mood based on capacity
        // If there's unprocessed grief, force sad mood regardless of capacity
        const capacity = 1 + this.getAgingRateModifier();
        let outlineColor;
        let mood = 'neutral';
        
        if (hasUnprocessedGrief) {
            // Force sad mood when there's unprocessed grief
            outlineColor = CONFIG.COLOR_JANE_SAD;
            mood = 'sad';
        } else if (capacity > 1.0) {
            // Only show happy mood if no unprocessed grief
            outlineColor = CONFIG.COLOR_JANE_HAPPY;
            mood = 'happy';
        } else if (capacity < 1.0) {
            outlineColor = CONFIG.COLOR_JANE_SAD;
            mood = 'sad';
        } else {
            // Neutral: use a darker shade of the base color
            outlineColor = darkenColor(CONFIG.COLOR_JANE, 0.25); // 25% darker
            mood = 'neutral';
        }

        ctx.save();

        // Draw trail particles first (behind Jane)
        this.trailParticles.forEach(p => {
            const pScreen = camera.worldToScreen(p.x, p.y);
            ctx.globalAlpha = p.alpha * 0.7;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(pScreen.x, pScreen.y, p.size, 0, Math.PI * 2);
            ctx.fill();
        });

        // Always keep Jane at full opacity (minimum 0.9, never too transparent)
        ctx.globalAlpha = Math.max(0.9, Math.min(brightness, 1.0));

        // Animation based on mood and movement
        let bobOffset = 0;
        let scaleX = 1;
        let scaleY = 1;
        let rotation = 0;

        if (isMoving) {
            if (mood === 'happy') {
                // Energetic bouncy walk
                bobOffset = Math.sin(time * 12) * 3;
                scaleX = 1 + Math.sin(time * 12) * 0.06;
                scaleY = 1 - Math.sin(time * 12) * 0.06;
            } else if (mood === 'sad') {
                // Slower, heavier walk
                bobOffset = Math.sin(time * 6) * 2;
                scaleX = 1 + Math.sin(time * 6) * 0.02;
                scaleY = 1 - Math.sin(time * 6) * 0.02;
                // Slight droop forward
                rotation = 0.04;
            } else {
                // Normal walk
                bobOffset = Math.sin(time * 9) * 2.5;
                scaleX = 1 + Math.sin(time * 9) * 0.04;
                scaleY = 1 - Math.sin(time * 9) * 0.04;
            }
        } else {
            // Standing animation (breathing)
            if (mood === 'happy') {
                // Upbeat idle - slight sway
                const sway = Math.sin(time * 2) * 0.02;
                scaleX = 1 + Math.sin(time * 3) * 0.04;
                scaleY = 1 + Math.sin(time * 3) * 0.04;
                rotation = sway;
            } else if (mood === 'sad') {
                // Dejected idle - slower, droopy
                scaleX = 1 + Math.sin(time * 1.5) * 0.02;
                scaleY = 1 + Math.sin(time * 1.5) * 0.02;
                rotation = 0.03; // Slight constant droop
                bobOffset = 2; // Lower position
            } else {
                // Normal breathing
                scaleX = 1 + Math.sin(time * 2.5) * 0.03;
                scaleY = 1 + Math.sin(time * 2.5) * 0.03;
            }
        }

        // Apply transforms
        ctx.translate(screenPos.x, screenPos.y + bobOffset);
        ctx.rotate(rotation);
        ctx.scale(scaleX, scaleY);

        // Draw Jane as a circle
        ctx.fillStyle = CONFIG.COLOR_JANE;
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.fill();

        // Glowing outline effect (mood-based, drawn first)
        // For neutral mood, use same color as fill so it blends in (no visible outline)
        if (mood !== 'neutral') {
            ctx.shadowColor = outlineColor;
            ctx.shadowBlur = mood === 'happy' ? 22 : (mood === 'sad' ? 12 : 18);
            ctx.lineWidth = mood === 'happy' ? 3 : 2.5; // Slightly thinner outline
            ctx.strokeStyle = outlineColor;
            ctx.beginPath();
            ctx.arc(0, 0, this.size, 0, Math.PI * 2);
            ctx.stroke();
        }

        // White outline (outside the mood outline)
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, this.size + 3, 0, Math.PI * 2); // Outside the mood outline
        ctx.stroke();

        // Happy sparkle effect
        if (mood === 'happy' && !isMoving) {
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 0.6 + Math.sin(time * 5) * 0.3;
            const sparkleSize = 3 + Math.sin(time * 7) * 1;
            ctx.fillStyle = '#FFD700';
            // Draw small sparkles around Jane
            for (let i = 0; i < 3; i++) {
                const angle = (time * 2 + i * Math.PI * 2 / 3);
                const dist = this.size + 8 + Math.sin(time * 4 + i) * 3;
                const sx = Math.cos(angle) * dist;
                const sy = Math.sin(angle) * dist;
                ctx.beginPath();
                ctx.arc(sx, sy, sparkleSize, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Sad effect - subtle grey aura
        if (mood === 'sad') {
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 0.15;
            ctx.fillStyle = '#555';
            ctx.beginPath();
            ctx.arc(0, 3, this.size + 8, 0, Math.PI * 2);
            ctx.fill();
        }

        // Reset scale for text (like Person class)
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.translate(screenPos.x, screenPos.y + bobOffset);

        // Draw Jane's name label inside circle
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#000'; // Black
        ctx.font = '11px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Jane', 0, 0);

        ctx.restore();
    }
}

// Main game class
class Game {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');

        // Set canvas size
        this.canvas.width = CONFIG.CANVAS_WIDTH;
        this.canvas.height = CONFIG.CANVAS_HEIGHT;

        // Game state
        this.state = 'start'; // 'start', 'playing', 'ended'
        this.lastTime = performance.now();

        // Initialize camera
        this.camera = new Camera(
            CONFIG.WORLD_WIDTH,
            CONFIG.WORLD_HEIGHT,
            this.canvas.width,
            this.canvas.height
        );

        // Initialize tile map
        this.tileMap = new TileMap(CONFIG.WORLD_WIDTH, CONFIG.WORLD_HEIGHT, 80);

        // Initialize player at start of world (accounting for border tiles)
        const borderSize = 80; // Tile size
        this.player = new Player(borderSize + 20, CONFIG.WORLD_HEIGHT / 2);

        // Initialize parents in trail
        this.initializeParents();

        // Initialize encounters
        this.encounters = [];
        this.initializeEncounters();

        // Initialize experiences
        this.experiences = [];
        this.initializeExperiences();

        // Initialize graves
        this.graves = [];

        // Death events tracking
        this.parent1Died = false;
        this.parent2Died = false;
        this.partnerDied = false;
        
        // Track which parent dies first (randomized)
        this.firstParentToDie = null; // 'mom' or 'dad'
        this.secondParentToDie = null; // 'mom' or 'dad'

        // Randomized death ages (calculated at game start)
        // Parents: 40% to 50% of BASE_LIFESPAN (80 years = 32 to 40 years)
        // Partner: 75% to 85% of BASE_LIFESPAN (80 years = 60 to 68 years)
        this.initializeDeathAges();

        // Child name pools (ensure unique names for each child)
        this.child1Names = GAME_DATA.names.child1Pool;
        this.child2Names = GAME_DATA.names.child2Pool;

        // Debug mode
        this.debugMode = false;

        // Interaction hint tracking
        this.hasShownInteractionHint = false;

        // End screen timing - track when end screen was shown to prevent immediate input
        this.endScreenShownTime = null;
        
        // Start screen timing - track when start screen was shown to prevent immediate input
        this.startScreenShownTime = performance.now();
        
        // Input mode: 'keyboard' or 'mouse' - determines if mouse movement is enabled
        this.inputMode = null; // Will be set when game starts

        // Special moment notifications (wedding, family complete)
        this.lastSpecialMoment = null;
        this.lastSpecialMomentTime = null;

        // Particle system for visual effects
        this.particles = [];

        // Start screen particles for eye candy
        this.startScreenParticles = [];
        this.initializeStartScreenParticles();

        // Death notification (gentle message when family passes)
        this.deathNotification = null;
        this.deathNotificationTime = null;

        // Anniversary tracking (years with partner)
        this.partnerJoinedAge = null; // Age when partner was accepted
        this.triggeredAnniversaries = new Set(); // Which milestones have been shown

        // Experience achievements tracking
        this.triggeredAchievements = new Set(); // Which achievements have been shown

        // Achievement notification
        this.achievementNotification = null;
        this.achievementNotificationTime = null;
        this.achievementNotificationType = null; // 'nature', 'knowledge', 'pleasure', or 'anniversary'

        // Grave visiting state (now handled by unified interaction system)
        this.graveVisitParticleTimer = 0; // Timer for spawning particles during grave visit

        // World desaturation for grief
        this.targetDesaturation = 0; // Target level (0-1)
        this.currentDesaturation = 0; // Current interpolated level

        // Initialize audio system
        this.initializeAudio();
    }

    initializeAudio() {
        // Create audio objects for both tracks
        this.track1 = new Audio('assets/track1.mp3');
        this.track2 = new Audio('assets/track2.mp3');
        
        // Set volume (adjust as needed, 0.0 to 1.0)
        this.track1.volume = 0.2;
        this.track2.volume = 0.2;
        
        // Set loop property to false (we handle looping manually)
        this.track1.loop = false;
        this.track2.loop = false;
        
        // Set up sequential playback: track1 -> track2 -> track1 -> ...
        this.track1.addEventListener('ended', () => {
            if (!this.muted) {
                this.track2.play().catch(err => {
                    console.log('Could not play track2:', err);
                });
            }
        });
        
        this.track2.addEventListener('ended', () => {
            if (!this.muted) {
                this.track1.play().catch(err => {
                    console.log('Could not play track1:', err);
                });
            }
        });
        
        // Track if music has been started (for autoplay handling)
        this.musicStarted = false;
        
        // Mute state - will be determined by autoplay attempt
        this.muted = true; // Default to muted, will update if autoplay succeeds
        this.userExplicitlyMuted = false; // Track if user explicitly muted (vs autoplay blocked)
        
        // Set up mouse click handling for mute button
        this.setupMuteButton();
        
        // Try to start music (will adapt to browser autoplay policy)
        this.attemptAutoplay();
    }
    
    attemptAutoplay() {
        // Try to start music - if browser allows autoplay, it will succeed
        // If blocked, we'll stay muted and user can enable via button
        this.track1.play().then(() => {
            // Autoplay succeeded - only unmute if user didn't explicitly mute
            if (!this.userExplicitlyMuted) {
                this.muted = false;
            }
            this.musicStarted = true;
            this.track1.volume = 0.2;
            this.track2.volume = 0.2;
        }).catch(err => {
            // Autoplay blocked - stay muted, user can enable via button
            // Only set muted if user didn't explicitly unmute (they might have clicked unmute before autoplay finished)
            if (!this.userExplicitlyMuted) {
                this.muted = true;
            }
            this.musicStarted = false;
            // Pause tracks to ensure they're stopped
            this.track1.pause();
            this.track2.pause();
            this.track1.currentTime = 0;
            this.track2.currentTime = 0;
        });
    }

    setupMuteButton() {
        this.canvas.addEventListener('click', (e) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Mute button area: top right, 40x40 pixels
            const buttonSize = 40;
            const buttonX = this.canvas.width - buttonSize - 10;
            if (x >= buttonX && x <= buttonX + buttonSize && y >= 10 && y <= 50) {
                this.toggleMute();
            }
        });
    }

    toggleMute() {
        this.muted = !this.muted;
        // Track user's explicit choice
        this.userExplicitlyMuted = this.muted;
        
        const volume = this.muted ? 0 : 0.2;
        this.track1.volume = volume;
        this.track2.volume = volume;
        
        // If unmuting and music hasn't started, try to start it
        if (!this.muted && !this.musicStarted) {
            this.startMusic();
        }
        
        // If unmuting and tracks are paused, resume playback
        if (!this.muted) {
            if (this.track1.paused && this.track2.paused) {
                this.track1.play().catch(err => {
                    console.log('Could not play track1:', err);
                });
            }
        } else {
            // If muting, pause both tracks
            this.track1.pause();
            this.track2.pause();
        }
    }

    drawMuteButton() {
        const buttonSize = 40;
        const buttonX = this.canvas.width - buttonSize - 10; // Top right
        const buttonY = 10;
        const radius = 6;

        this.ctx.save();

        // Determine if music is actually playing (not just unmuted)
        const isActuallyPlaying = this.isMusicPlaying();
        
        // Button background
        this.ctx.fillStyle = (this.muted || !isActuallyPlaying) ? 'rgba(80, 80, 80, 0.9)' : 'rgba(100, 100, 100, 0.8)';
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        this.ctx.shadowBlur = 4;
        this.ctx.shadowOffsetY = 2;

        // Rounded rectangle
        this.ctx.beginPath();
        this.ctx.moveTo(buttonX + radius, buttonY);
        this.ctx.lineTo(buttonX + buttonSize - radius, buttonY);
        this.ctx.quadraticCurveTo(buttonX + buttonSize, buttonY, buttonX + buttonSize, buttonY + radius);
        this.ctx.lineTo(buttonX + buttonSize, buttonY + buttonSize - radius);
        this.ctx.quadraticCurveTo(buttonX + buttonSize, buttonY + buttonSize, buttonX + buttonSize - radius, buttonY + buttonSize);
        this.ctx.lineTo(buttonX + radius, buttonY + buttonSize);
        this.ctx.quadraticCurveTo(buttonX, buttonY + buttonSize, buttonX, buttonY + buttonSize - radius);
        this.ctx.lineTo(buttonX, buttonY + radius);
        this.ctx.quadraticCurveTo(buttonX, buttonY, buttonX + radius, buttonY);
        this.ctx.closePath();
        this.ctx.fill();

        this.ctx.shadowBlur = 0;

        // Border (black outline to distinguish from game elements)
        this.ctx.strokeStyle = '#000';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();

        // Speaker icon (simple representation)
        this.ctx.strokeStyle = '#fff';
        this.ctx.fillStyle = '#fff';
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';

        const centerX = buttonX + buttonSize / 2;
        const centerY = buttonY + buttonSize / 2;

        // Draw speaker icon (always shown)
        // Speaker base (rectangle)
        this.ctx.fillRect(centerX - 8, centerY - 4, 4, 8);
        // Sound waves (curved lines)
        this.ctx.beginPath();
        this.ctx.arc(centerX - 2, centerY, 6, 0.3, 1.2, false);
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.arc(centerX - 2, centerY, 10, 0.2, 1.3, false);
        this.ctx.stroke();

        // If muted or not playing, draw diagonal line through speaker
        if (this.muted || !isActuallyPlaying) {
            this.ctx.strokeStyle = '#fff';
            this.ctx.lineWidth = 3;
            this.ctx.beginPath();
            // Diagonal line from top-left to bottom-right of speaker area
            this.ctx.moveTo(centerX - 10, centerY - 8);
            this.ctx.lineTo(centerX + 8, centerY + 8);
            this.ctx.stroke();
        }

        this.ctx.restore();
    }

    startMusic() {
        // Only try to start if music hasn't started yet and not muted
        if (!this.musicStarted && !this.muted && this.track1.paused && this.track2.paused) {
            this.track1.play().then(() => {
                this.musicStarted = true;
                this.muted = false; // Update mute state if music successfully starts
            }).catch(err => {
                // Autoplay blocked - keep muted state
                console.log('Audio autoplay blocked. Click mute button to enable music.');
                this.muted = true;
            });
        }
    }
    
    isMusicPlaying() {
        // Check if either track is actually playing
        return !this.track1.paused || !this.track2.paused;
    }

    initializeDeathAges() {
        // Death ages are now absolute ages (Jane's age when they die)
        // Using 80 as the full life expectancy baseline for calculations
        const fullLifespan = 80;

        // Calculate death ages for first and second parent (ranges stay the same)
        // First parent: dies when Jane is 32-37 years old (40-46% of 80)
        const firstParentMinAge = fullLifespan * 0.40; // 32 years
        const firstParentMaxAge = fullLifespan * 0.46; // 36.8 years
        const firstParentDeathAge = randomRange(firstParentMinAge, firstParentMaxAge);

        // Second parent: dies when Jane is 40-44 years old (50-55% of 80)
        // Ensure second parent dies AFTER first parent (minimum 3 years gap)
        const secondParentMinAge = Math.max(fullLifespan * 0.50, firstParentDeathAge + 3); // At least 40, or firstParentAge + 3
        const secondParentMaxAge = fullLifespan * 0.55; // 44 years
        const secondParentDeathAge = randomRange(secondParentMinAge, secondParentMaxAge);

        // Randomly assign which parent (Mom or Dad) dies first
        const momDiesFirst = Math.random() < 0.5;
        
        if (momDiesFirst) {
            this.firstParentToDie = 'mom';
            this.secondParentToDie = 'dad';
            this.parent1DeathAge = firstParentDeathAge; // Mom dies first
            this.parent2DeathAge = secondParentDeathAge; // Dad dies second
        } else {
            this.firstParentToDie = 'dad';
            this.secondParentToDie = 'mom';
            this.parent1DeathAge = firstParentDeathAge; // Dad dies first
            this.parent2DeathAge = secondParentDeathAge; // Mom dies second
        }

        // Partner: dies when Jane is 72-80 years old (90-100% of 80)
        // This allows for 50+ years of marriage if married at ~20
        const partnerMinAge = fullLifespan * 0.90; // 72 years
        const partnerMaxAge = fullLifespan * 1.00; // 80 years
        this.partnerDeathAge = randomRange(partnerMinAge, partnerMaxAge);
    }

    initializeParents() {
        // Create Mom and Dad - store references for death handling
        const mom = new Person('parent', CONFIG.COLOR_MOM,
            this.player.x - CONFIG.TRAIL_SPACING, this.player.y, GAME_DATA.names.parent1);
        const dad = new Person('parent', CONFIG.COLOR_DAD,
            this.player.x - CONFIG.TRAIL_SPACING * 2, this.player.y, GAME_DATA.names.parent2);

        // Store references to identify which parent dies
        this.momPerson = mom;
        this.dadPerson = dad;

        this.player.addPerson(mom);
        this.player.addPerson(dad);
    }

    initializeEncounters() {
        // Partner encounter
        this.encounters.push(new Encounter(
            'partner',
            CONFIG.WORLD_WIDTH * CONFIG.PARTNER_ENCOUNTER,
            CONFIG.WORLD_HEIGHT / 2
        ));

        // Child encounters (will only be available if partner is accepted)
        this.encounters.push(new Encounter(
            'child1',
            CONFIG.WORLD_WIDTH * CONFIG.CHILD1_ENCOUNTER,
            CONFIG.WORLD_HEIGHT / 2 + 100
        ));

        this.encounters.push(new Encounter(
            'child2',
            CONFIG.WORLD_WIDTH * CONFIG.CHILD2_ENCOUNTER,
            CONFIG.WORLD_HEIGHT / 2 - 100
        ));
    }

    initializeExperiences() {
        const types = ['nature', 'knowledge', 'pleasure'];

        for (let i = 0; i < CONFIG.EXPERIENCE_COUNT; i++) {
            const type = types[randomInt(0, types.length)];

            // Distribute experiences across the world, avoiding the very start and end
            const x = randomRange(CONFIG.WORLD_WIDTH * 0.1, CONFIG.WORLD_WIDTH * 0.9);
            const y = randomRange(CONFIG.WORLD_HEIGHT * 0.2, CONFIG.WORLD_HEIGHT * 0.8);

            this.experiences.push(new Experience(type, x, y));
        }
    }

    update() {
        const currentTime = performance.now();
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        if (this.state === 'ended' || this.state === 'start') {
            return;
        }

        // Update player (pass debug mode for faster aging)
        // Update input mode in Input system so player can access it
        Input.setInputMode(this.inputMode);
        
        // Player movement is handled by isCollecting flag in player.update()
        this.player.update(deltaTime, this.debugMode);

        // Update camera to follow player
        this.camera.follow(this.player.x, this.player.y);

        // Update trail
        this.updateTrail();

        // Update proximity effects for experiences, encounters, and graves
        this.experiences.forEach(exp => exp.updateProximity(this.player.x, this.player.y));
        this.encounters.forEach(enc => enc.updateProximity(this.player.x, this.player.y));
        this.graves.forEach(grave => grave.updateProximity(this.player.x, this.player.y));

        // Update particles
        this.updateParticles(deltaTime);

        // Spawn particles during grave visit (while waiting)
        if (this.player.isCollecting && this.player.collectingGrave) {
            this.spawnGraveVisitParticles(this.player.collectingGrave, deltaTime);
        }

        // Update start screen particles if on start screen
        if (this.state === 'start') {
            this.updateStartScreenParticles(deltaTime);
        }

        if (this.state === 'ended' || this.state === 'start') {
            return;
        }

        // Check for death events
        this.checkDeathEvents();

        // Check for encounters
        this.checkEncounters();

        // Check for experience collection
        this.checkExperiences();

        // Check for grave interactions (enhanced)
        this.checkGravesEnhanced(deltaTime);

        // Handle completed interactions
        this.handleCompletedInteractions();

        // Check for anniversary milestones
        this.checkAnniversaries();

        // Check for experience achievements
        this.checkExperienceAchievements();

        // Update world desaturation based on grief
        this.updateDesaturation(deltaTime);

        // Check if life has ended (when remaining life reaches 0)
        if (this.player.remainingLife <= 0) {
            this.endGame();
        }
    }

    updateTrail() {
        let prevX = this.player.x;
        let prevY = this.player.y;

        // Sort living people: partner first, then children, then parents
        const sortedPeople = [...this.player.livingPeople].sort((a, b) => {
            // Partner (Taylor) comes first
            if (a.type === 'partner' && b.type !== 'partner') return -1;
            if (a.type !== 'partner' && b.type === 'partner') return 1;
            
            // Children come after partner
            if (a.type === 'child' && b.type === 'parent') return -1;
            if (a.type === 'parent' && b.type === 'child') return 1;
            
            // Maintain relative order within same type
            return 0;
        });

        // Update living people in sorted order
        sortedPeople.forEach((person) => {
            const angle = Math.atan2(prevY - person.y, prevX - person.x);
            const targetX = prevX - Math.cos(angle) * CONFIG.TRAIL_SPACING;
            const targetY = prevY - Math.sin(angle) * CONFIG.TRAIL_SPACING;

            person.update(targetX, targetY);

            prevX = person.x;
            prevY = person.y;
        });

        // Update memories (departed ones always at the end, regardless of type)
        this.player.memories.forEach((memory) => {
            const angle = Math.atan2(prevY - memory.y, prevX - memory.x);
            const targetX = prevX - Math.cos(angle) * CONFIG.TRAIL_SPACING;
            const targetY = prevY - Math.sin(angle) * CONFIG.TRAIL_SPACING;

            memory.update(targetX, targetY, performance.now() / 1000);

            prevX = memory.x;
            prevY = memory.y;
        });
    }

    checkDeathEvents() {
        const age = this.player.age;

        // Parent 1 death
        if (!this.parent1Died && age >= this.parent1DeathAge) {
            this.parent1Died = true;
            this.handleDeath('parent1');
        }

        // Parent 2 death
        if (!this.parent2Died && age >= this.parent2DeathAge) {
            this.parent2Died = true;
            this.handleDeath('parent2');
        }

        // Partner death (only if partner exists)
        const hasPartner = this.player.livingPeople.some(p => p.type === 'partner');
        if (!this.partnerDied && hasPartner && age >= this.partnerDeathAge) {
            this.partnerDied = true;
            this.handleDeath('partner');
        }
    }

    handleDeath(type) {
        // Find the person who died and get their position
        let deadPerson = null;
        let deadPersonIndex = -1;
        
        if (type === 'parent1' || type === 'parent2') {
            // Determine which parent (Mom or Dad) should die based on randomization
            let targetParentName = null;
            if (type === 'parent1') {
                // First parent to die
                targetParentName = this.firstParentToDie === 'mom' ? GAME_DATA.names.parent1 : GAME_DATA.names.parent2;
            } else {
                // Second parent to die
                targetParentName = this.secondParentToDie === 'mom' ? GAME_DATA.names.parent1 : GAME_DATA.names.parent2;
            }
            
            // Find the specific parent by name
            deadPersonIndex = this.player.livingPeople.findIndex(p => 
                p.type === 'parent' && p.name === targetParentName
            );
        } else {
            deadPersonIndex = this.player.livingPeople.findIndex(p => p.type === type);
        }

        if (deadPersonIndex !== -1) {
            deadPerson = this.player.livingPeople[deadPersonIndex];
            // Remove from living people
            this.player.livingPeople.splice(deadPersonIndex, 1);
        }

        // Add to memories (for the trail)
        // Memory type should map correctly: parent1 -> Mom memories, parent2 -> Dad memories
        // Map based on which parent actually died (Mom or Dad), not death order
        let memoryType = type;
        if (type === 'parent1' || type === 'parent2') {
            // Determine which parent (Mom or Dad) died and map to correct memory type
            let targetParentName = null;
            if (type === 'parent1') {
                // First parent to die
                targetParentName = this.firstParentToDie === 'mom' ? GAME_DATA.names.parent1 : GAME_DATA.names.parent2;
            } else {
                // Second parent to die
                targetParentName = this.secondParentToDie === 'mom' ? GAME_DATA.names.parent1 : GAME_DATA.names.parent2;
            }
            
            // Map to memory type: Mom -> 'parent1', Dad -> 'parent2'
            memoryType = targetParentName === GAME_DATA.names.parent1 ? 'parent1' : 'parent2';
        }
        
        const memory = new Memory(memoryType);
        memory.x = this.player.x - CONFIG.TRAIL_SPACING;
        memory.y = this.player.y;
        this.player.addMemory(memory);

        // Create a grave at the position where they died
        if (deadPerson) {
            const grave = new Grave(memory, deadPerson.x, deadPerson.y);
            this.graves.push(grave);
            // Show gentle death notification
            // Use proper name for partner (Taylor instead of Husband)
            const displayName = deadPerson.type === 'partner' ? GAME_DATA.names.partner : deadPerson.name;
            this.showDeathNotification(displayName, deadPerson.type);
        } else {
            // Fallback: create grave at player position if person not found
            const grave = new Grave(memory, this.player.x, this.player.y);
            this.graves.push(grave);
        }
    }

    checkEncounters() {
        if (this.player.isCollecting) return;

        // Check if player can interact with any encounter
        this.encounters.forEach(encounter => {
            if (!encounter.canInteract(this.player.x, this.player.y)) return;

            // Check if encounter should be available
            // Children require partner, but can be interacted with in any order
            if (encounter.type === 'child1' || encounter.type === 'child2') {
                const hasPartner = this.player.livingPeople.some(p => p.type === 'partner');
                if (!hasPartner) return;
            }

            // If player presses E/Space or clicks, start interaction (2 second wait)
            if (Input.isInteracting()) {
                this.player.startInteracting(encounter);
                this.hasShownInteractionHint = true;
                // Consume mouse click if it was used
                Input.consumeClick();
            }
        });

    }

    checkExperiences() {
        if (this.player.isCollecting) return;

        this.experiences.forEach(exp => {
            if (exp.canInteract(this.player.x, this.player.y)) {
            if (Input.isInteracting()) {
                    this.player.startCollecting(exp);
                    // Mark interaction hint as shown (first interaction)
                    this.hasShownInteractionHint = true;
                    // Consume mouse click if it was used
                    Input.consumeClick();
                }
            }
        });
    }

    checkGraves() {
        // Legacy method - kept for compatibility
        if (this.player.isCollecting) return;

        this.graves.forEach(grave => {
            if (grave.canInteract(this.player.x, this.player.y)) {
                if (Input.isInteracting()) {
                    grave.process();
                    this.player.memoryVisitCount++;
                    this.hasShownInteractionHint = true;
                    // Consume mouse click if it was used
                    Input.consumeClick();
                }
            }
        });
    }

    // Enhanced grave visiting system - unified with other interactions (3 second wait, no holding)
    checkGravesEnhanced(deltaTime) {
        if (this.player.isCollecting) return;

        // Update all graves
        this.graves.forEach(grave => grave.update(deltaTime));

        // Check if player is near any grave (can always visit, even processed ones)
        for (const grave of this.graves) {
            if (grave.canInteract(this.player.x, this.player.y)) {
                if (Input.isInteracting()) {
                    this.player.startVisiting(grave);
                    this.hasShownInteractionHint = true;
                    // Consume mouse click if it was used
                    Input.consumeClick();
                    break;
                }
            }
        }
    }

    // Handle completed interactions (encounters and graves)
    handleCompletedInteractions() {
        // Check if encounter interaction completed
        if (this.player.justCompletedEncounter) {
            const encounter = this.player.justCompletedEncounter;
            this.player.justCompletedEncounter = null;
                encounter.accept();

                // Determine the name pool for children
                let namePool = null;
                if (encounter.type === 'child1') {
                    namePool = this.child1Names;
                } else if (encounter.type === 'child2') {
                    namePool = this.child2Names;
                }

                const person = new Person(
                    encounter.type === 'partner' ? 'partner' : 'child',
                    encounter.color,
                    encounter.x,
                    encounter.y,
                    null,
                    namePool
                );
                // Mark partner as joined so label changes to 'Taylor'
                if (encounter.type === 'partner') {
                    person.joined = true;
                    // Track when partner joined for anniversary system
                    this.partnerJoinedAge = this.player.age;
                }
                // Check if this is the first child or second child BEFORE adding
                let isFirstChild = false;
                if (encounter.type === 'child1' || encounter.type === 'child2') {
                    const hasAnyChild = this.player.livingPeople.some(p => p.type === 'child');
                    isFirstChild = !hasAnyChild;
                }
                
                this.player.addPerson(person);
                // Special moment notifications and FX
                if (encounter.type === 'partner') {
                    this.lastSpecialMoment = 'wedding';
                    this.lastSpecialMomentTime = Date.now();
                    this.spawnSpecialEventParticles('wedding');
                } else if (encounter.type === 'child1' || encounter.type === 'child2') {
                    if (isFirstChild) {
                        // First child
                        this.lastSpecialMoment = 'firstchild';
                        this.lastSpecialMomentTime = Date.now();
                        this.spawnSpecialEventParticles('firstchild');
                    } else {
                        // Second child (family complete)
                        this.lastSpecialMoment = 'family';
                        this.lastSpecialMomentTime = Date.now();
                        this.spawnSpecialEventParticles('family');
                    }
                }
        }

        // Check if grave visit completed
        if (this.player.justCompletedGrave) {
            const grave = this.player.justCompletedGrave;
            this.player.justCompletedGrave = null;
            this.graveVisitParticleTimer = 0; // Reset particle timer
            const wasFirstTime = !grave.processed;
            grave.completeVisit();
            
            if (wasFirstTime) {
                // First time processing - gives grief relief benefit
                this.player.memoryVisitCount++;
            }
            // Always spawn particles and show memory
            this.spawnGraveParticles(grave);
        }
    }

    // Spawn particles continuously during grave visit (while waiting 3 seconds)
    spawnGraveVisitParticles(grave, deltaTime) {
        // Spawn particles periodically during the visit
        if (!this.graveVisitParticleTimer) {
            this.graveVisitParticleTimer = 0;
        }
        this.graveVisitParticleTimer += deltaTime;

        // Spawn particles every 0.15 seconds
        if (this.graveVisitParticleTimer >= 0.15) {
            this.graveVisitParticleTimer = 0;
            const screenPos = this.camera.worldToScreen(grave.x, grave.y);
            const colors = ['#4ECDC4', '#5ECFB0', '#6BCF7F', '#7DCEA0', '#98D8C8', '#87CEEB', '#ADD8E6']; // Cool green/teal/blue colors

            // Spawn 3-5 particles per interval
            const count = 3 + Math.floor(Math.random() * 3);
            for (let i = 0; i < count; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 20 + Math.random() * 30;
                this.particles.push({
                    x: screenPos.x + (Math.random() - 0.5) * 20,
                    y: screenPos.y + (Math.random() - 0.5) * 20,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - 40,
                    gravity: -15, // Float upward gently
                    size: 2 + Math.random() * 3,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    life: 0.8 + Math.random() * 0.5,
                    maxLife: 0.8 + Math.random() * 0.5,
                    alpha: 1
                });
            }
        }
    }

    // Spawn gentle particles when grave visit completes (cool green colors)
    spawnGraveParticles(grave) {
        const screenPos = this.camera.worldToScreen(grave.x, grave.y);
        const colors = ['#4ECDC4', '#5ECFB0', '#6BCF7F', '#7DCEA0', '#98D8C8']; // Cool green/teal colors

        for (let i = 0; i < 20; i++) {
            const angle = (Math.PI * 2 * i / 20);
            const speed = 30 + Math.random() * 40;
            this.particles.push({
                x: screenPos.x,
                y: screenPos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 60,
                gravity: -20, // Float upward
                size: 3 + Math.random() * 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1.2 + Math.random() * 0.6,
                maxLife: 1.2 + Math.random() * 0.6,
                alpha: 1
            });
        }
    }

    // Check for anniversary milestones with partner
    checkAnniversaries() {
        if (this.partnerJoinedAge === null) return; // No partner yet
        
        // Check if partner is still alive
        const hasPartner = this.player.livingPeople.some(p => p.type === 'partner');
        if (!hasPartner) return; // Partner has passed

        const yearsWithPartner = this.player.age - this.partnerJoinedAge;
        const milestones = [10, 20, 30, 40, 50];

        for (const milestone of milestones) {
            if (yearsWithPartner >= milestone && !this.triggeredAnniversaries.has(milestone)) {
                this.triggeredAnniversaries.add(milestone);
                this.showAchievementNotification(`${milestone} years together`, 'anniversary');
                this.spawnAnniversaryParticles();
                break; // Only show one at a time
            }
        }
    }

    // Spawn warm golden particles for anniversary
    spawnAnniversaryParticles() {
        const screenPos = this.camera.worldToScreen(this.player.x, this.player.y);
        const colors = ['#FFD700', '#FFC0CB', '#FF69B4', '#FFDAB9', '#FFE4B5'];

        for (let i = 0; i < 30; i++) {
            const angle = (Math.PI * 2 * i / 30) + Math.random() * 0.3;
            const speed = 60 + Math.random() * 80;
            this.particles.push({
                x: screenPos.x,
                y: screenPos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 50,
                gravity: 60,
                size: 4 + Math.random() * 4,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 0.9 + Math.random() * 0.5,
                maxLife: 0.9 + Math.random() * 0.5,
                alpha: 1
            });
        }
    }

    // Check for experience category achievements
    checkExperienceAchievements() {
        const counts = {
            nature: this.player.experiences.filter(e => e.type === 'nature').length,
            knowledge: this.player.experiences.filter(e => e.type === 'knowledge').length,
            pleasure: this.player.experiences.filter(e => e.type === 'pleasure').length
        };

        const achievements = GAME_DATA.experienceAchievements;
        const thresholds = [3, 5]; // Check in order

        for (const type of ['nature', 'knowledge', 'pleasure']) {
            for (const threshold of thresholds) {
                const achievementKey = `${type}_${threshold}`;
                if (counts[type] >= threshold && !this.triggeredAchievements.has(achievementKey)) {
                    this.triggeredAchievements.add(achievementKey);
                    const message = achievements[type][threshold];
                    if (message) {
                        this.showAchievementNotification(message, type);
                        this.spawnAchievementParticles(type);
                    }
                    break; // Only show one achievement at a time per category
                }
            }
        }
    }

    // Show achievement notification
    showAchievementNotification(message, type) {
        this.achievementNotification = message;
        this.achievementNotificationTime = Date.now();
        this.achievementNotificationType = type;
    }

    // Spawn particles for achievement based on type
    spawnAchievementParticles(type) {
        const screenPos = this.camera.worldToScreen(this.player.x, this.player.y);
        let colors;

        if (type === 'nature') {
            colors = ['#82E0AA', '#58D68D', '#2ECC71', '#27AE60', '#1ABC9C'];
        } else if (type === 'knowledge') {
            colors = ['#5DADE2', '#85C1E9', '#3498DB', '#2980B9', '#AF7AC5'];
        } else if (type === 'pleasure') {
            colors = ['#FF6B8A', '#FF8C5A', '#FFB347', '#F7DC6F', '#F1948A'];
        } else {
            colors = ['#FFD700', '#FFC0CB', '#FF69B4', '#FFDAB9'];
        }

        for (let i = 0; i < 25; i++) {
            const angle = (Math.PI * 2 * i / 25) + Math.random() * 0.3;
            const speed = 50 + Math.random() * 70;
            this.particles.push({
                x: screenPos.x,
                y: screenPos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 40,
                gravity: 70,
                size: 4 + Math.random() * 4,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 0.8 + Math.random() * 0.5,
                maxLife: 0.8 + Math.random() * 0.5,
                alpha: 1
            });
        }
    }

    // Update world desaturation based on unprocessed grief
    updateDesaturation(deltaTime) {
        // Calculate target desaturation based on unprocessed memories
        let unprocessedCount = 0;
        this.player.memories.forEach(memory => {
            if (!memory.processed) {
                unprocessedCount++;
            }
        });

        // Each unprocessed death adds 20% desaturation, max 50%
        this.targetDesaturation = Math.min(0.5, unprocessedCount * 0.2);

        // Smoothly interpolate current desaturation
        const lerpSpeed = this.currentDesaturation < this.targetDesaturation ? 0.02 : 0.03;
        this.currentDesaturation = lerp(this.currentDesaturation, this.targetDesaturation, lerpSpeed);
    }

    // Particle system methods
    updateParticles(deltaTime) {
        // Check if experience was just collected - spawn particles
        if (this.player.justCollectedExperience) {
            this.player.justCollectedExperience = false;
            this.spawnExperienceParticles();
        }

        // Update existing particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.life -= deltaTime;
            if (p.life <= 0) {
                this.particles.splice(i, 1);
                continue;
            }
            p.x += p.vx * deltaTime;
            p.y += p.vy * deltaTime;
            p.vy += p.gravity * deltaTime;
            p.vx *= 0.98; // Air resistance
            p.alpha = Math.min(1, p.life / p.maxLife);
        }
    }

    spawnExperienceParticles() {
        // Use the actual color from the collected experience
        const baseColor = this.player.lastCollectedExperienceColor || '#FF8C6B';

        // Spawn burst of particles at player position
        const screenPos = this.camera.worldToScreen(this.player.x, this.player.y);
        for (let i = 0; i < 18; i++) {
            const angle = (Math.PI * 2 * i / 18) + Math.random() * 0.3;
            const speed = 70 + Math.random() * 100;
            this.particles.push({
                x: screenPos.x,
                y: screenPos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 40,
                gravity: 120,
                size: 4 + Math.random() * 5,
                color: baseColor,
                life: 0.7 + Math.random() * 0.5,
                maxLife: 0.7 + Math.random() * 0.5,
                alpha: 1
            });
        }

        // Add some white sparkles
        for (let i = 0; i < 6; i++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = 15 + Math.random() * 30;
            this.particles.push({
                x: screenPos.x + Math.cos(angle) * dist,
                y: screenPos.y + Math.sin(angle) * dist,
                vx: (Math.random() - 0.5) * 25,
                vy: -25 - Math.random() * 40,
                gravity: 15,
                size: 2 + Math.random() * 2,
                color: '#fff',
                life: 0.4 + Math.random() * 0.4,
                maxLife: 0.4 + Math.random() * 0.4,
                alpha: 1
            });
        }
    }

    // Spawn impressive particles for special life events (wedding, children)
    spawnSpecialEventParticles(eventType) {
        const screenPos = this.camera.worldToScreen(this.player.x, this.player.y);
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;

        let colors, particleCount, sparkleCount;

        if (eventType === 'wedding') {
            colors = ['#FFD700', '#FFC0CB', '#FF69B4', '#FFB6C1', '#FFDAB9', '#FFF8DC', '#FFFACD'];
            particleCount = 60;
            sparkleCount = 30;
        } else if (eventType === 'firstchild') {
            colors = ['#87CEEB', '#ADD8E6', '#B0E0E6', '#E0FFFF', '#F0F8FF', '#AFEEEE', '#98D8C8'];
            particleCount = 50;
            sparkleCount = 25;
        } else { // family
            colors = ['#FFD700', '#FFA500', '#FF8C00', '#FFDAB9', '#FFE4B5', '#F0E68C', '#FFFACD'];
            particleCount = 55;
            sparkleCount = 28;
        }

        // Main burst from player position
        for (let i = 0; i < particleCount; i++) {
            const angle = (Math.PI * 2 * i / particleCount) + Math.random() * 0.4;
            const speed = 100 + Math.random() * 180;
            this.particles.push({
                x: screenPos.x,
                y: screenPos.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 80,
                gravity: 100,
                size: 5 + Math.random() * 8,
                color: colors[Math.floor(Math.random() * colors.length)],
                life: 1.2 + Math.random() * 0.8,
                maxLife: 1.2 + Math.random() * 0.8,
                alpha: 1
            });
        }

        // Sparkles around the screen
        for (let i = 0; i < sparkleCount; i++) {
            const x = Math.random() * this.canvas.width;
            const y = Math.random() * this.canvas.height * 0.7;
            this.particles.push({
                x: x,
                y: y,
                vx: (Math.random() - 0.5) * 40,
                vy: -50 - Math.random() * 80,
                gravity: 30,
                size: 3 + Math.random() * 4,
                color: '#fff',
                life: 0.8 + Math.random() * 0.6,
                maxLife: 0.8 + Math.random() * 0.6,
                alpha: 1
            });
        }

        // Rising hearts/stars effect for wedding
        if (eventType === 'wedding') {
            for (let i = 0; i < 15; i++) {
                const x = screenPos.x + (Math.random() - 0.5) * 100;
                this.particles.push({
                    x: x,
                    y: screenPos.y + 20,
                    vx: (Math.random() - 0.5) * 20,
                    vy: -100 - Math.random() * 60,
                    gravity: -10, // Float upward
                    size: 6 + Math.random() * 4,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    life: 1.5 + Math.random() * 0.5,
                    maxLife: 1.5 + Math.random() * 0.5,
                    alpha: 1
                });
            }
        }
    }

    drawParticles() {
        this.ctx.save();
        this.particles.forEach(p => {
            this.ctx.globalAlpha = p.alpha * 0.9;
            this.ctx.fillStyle = p.color;
            this.ctx.beginPath();
            this.ctx.arc(p.x, p.y, p.size * p.alpha, 0, Math.PI * 2);
            this.ctx.fill();
        });
        this.ctx.restore();
    }

    // Death notification - gentle message
    showDeathNotification(personName, personType) {
        this.deathNotification = { name: personName, type: personType };
        this.deathNotificationTime = Date.now();
    }

    drawDeathNotification() {
        if (!this.deathNotification) return;

        const timeSince = (Date.now() - this.deathNotificationTime) / 1000;
        const duration = 4; // Show for 4 seconds

        if (timeSince >= duration) {
            this.deathNotification = null;
            this.deathNotificationTime = null;
            return;
        }

        // Gentle fade in and out
        let opacity = 1;
        if (timeSince < 0.5) opacity = timeSince / 0.5;
        else if (timeSince > duration - 0.8) opacity = (duration - timeSince) / 0.8;

        this.ctx.save();
        this.ctx.globalAlpha = opacity;

        // Soft vignette overlay
        const vignetteGrad = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2, 0,
            this.canvas.width / 2, this.canvas.height / 2, this.canvas.width * 0.7
        );
        vignetteGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignetteGrad.addColorStop(1, 'rgba(0, 0, 0, 0.15)');
        this.ctx.fillStyle = vignetteGrad;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Message box at bottom center - dark theme
        const boxWidth = 320;
        const boxHeight = 52;
        const boxX = this.canvas.width / 2 - boxWidth / 2;
        const boxY = this.canvas.height - 100;
        const radius = 10;

        // Stronger shadow for dark box
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        this.ctx.shadowBlur = 20;
        this.ctx.shadowOffsetY = 4;

        // Rounded rectangle - dark background
        this.ctx.beginPath();
        this.ctx.moveTo(boxX + radius, boxY);
        this.ctx.lineTo(boxX + boxWidth - radius, boxY);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
        this.ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - radius, boxY + boxHeight);
        this.ctx.lineTo(boxX + radius, boxY + boxHeight);
        this.ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - radius);
        this.ctx.lineTo(boxX, boxY + radius);
        this.ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
        this.ctx.closePath();

        // Dark gradient background
        const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
        grad.addColorStop(0, 'rgba(40, 40, 50, 0.95)');
        grad.addColorStop(1, 'rgba(30, 30, 40, 0.95)');
        this.ctx.fillStyle = grad;
        this.ctx.fill();

        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;

        // Border
        this.ctx.strokeStyle = 'rgba(100, 100, 110, 0.6)';
        this.ctx.lineWidth = 1.5;
        this.ctx.stroke();

        // Text - white for contrast
        this.ctx.fillStyle = '#ffffff';
        this.ctx.font = '17px Georgia';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(`${this.deathNotification.name} has passed`, this.canvas.width / 2, boxY + boxHeight / 2);

        this.ctx.restore();
    }

    // Draw achievement notification (for experience milestones and anniversaries)
    drawAchievementNotification() {
        if (!this.achievementNotification) return;

        const timeSince = (Date.now() - this.achievementNotificationTime) / 1000;
        const duration = 4.5; // Show for 4.5 seconds

        if (timeSince >= duration) {
            this.achievementNotification = null;
            this.achievementNotificationTime = null;
            this.achievementNotificationType = null;
            return;
        }

        // Fade in and out
        let opacity = 1;
        if (timeSince < 0.4) opacity = timeSince / 0.4;
        else if (timeSince > duration - 0.6) opacity = (duration - timeSince) / 0.6;

        this.ctx.save();
        this.ctx.globalAlpha = opacity;

        // Determine if we need extra height for anniversary title
        const isAnniversary = this.achievementNotificationType === 'anniversary';
        const boxWidth = 320;
        const boxHeight = isAnniversary ? 68 : 48; // Extra height for title
        const boxX = this.canvas.width / 2 - boxWidth / 2;
        const boxY = 180; // Below special moment notifications
        const radius = 10;

        // Gentle scale-in
        const scale = timeSince < 0.3 ? 0.9 + (timeSince / 0.3) * 0.1 : 1;
        const cx = this.canvas.width / 2;
        const cy = boxY + boxHeight / 2;
        this.ctx.translate(cx, cy);
        this.ctx.scale(scale, scale);
        this.ctx.translate(-cx, -cy);

        // Shadow
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
        this.ctx.shadowBlur = 12;
        this.ctx.shadowOffsetY = 4;

        // Rounded rectangle
        this.ctx.beginPath();
        this.ctx.moveTo(boxX + radius, boxY);
        this.ctx.lineTo(boxX + boxWidth - radius, boxY);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
        this.ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - radius, boxY + boxHeight);
        this.ctx.lineTo(boxX + radius, boxY + boxHeight);
        this.ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - radius);
        this.ctx.lineTo(boxX, boxY + radius);
        this.ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
        this.ctx.closePath();

        // Gradient based on type
        const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
        let textColor = '#444';
        let borderColor;

        if (this.achievementNotificationType === 'nature') {
            grad.addColorStop(0, '#e8f8f0');
            grad.addColorStop(1, '#c8e8d8');
            borderColor = 'rgba(80, 180, 120, 0.7)';
        } else if (this.achievementNotificationType === 'knowledge') {
            grad.addColorStop(0, '#e8f0f8');
            grad.addColorStop(1, '#c8d8e8');
            borderColor = 'rgba(80, 120, 180, 0.7)';
        } else if (this.achievementNotificationType === 'pleasure') {
            grad.addColorStop(0, '#f8f0e8');
            grad.addColorStop(1, '#e8d8c8');
            borderColor = 'rgba(200, 140, 100, 0.7)';
        } else { // anniversary
            grad.addColorStop(0, '#fff8e8');
            grad.addColorStop(1, '#ffe8c8');
            borderColor = 'rgba(200, 160, 80, 0.8)';
        }

        this.ctx.fillStyle = grad;
        this.ctx.fill();

        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;

        // Border
        this.ctx.strokeStyle = borderColor;
        this.ctx.lineWidth = 2;
        this.ctx.stroke();

        // Text
        this.ctx.fillStyle = textColor;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        
        if (isAnniversary) {
            // Draw "Anniversary" title
            this.ctx.font = 'bold 14px Georgia';
            this.ctx.fillText('Anniversary', cx, boxY + 20);
            
            // Draw notification text below title
            this.ctx.font = '17px Georgia';
            this.ctx.fillText(this.achievementNotification, cx, boxY + 48);
        } else {
            // Regular achievement notification (no title)
            this.ctx.font = '17px Georgia';
            this.ctx.fillText(this.achievementNotification, cx, cy);
        }

        this.ctx.restore();
    }

    // Start screen particle system
    initializeStartScreenParticles() {
        this.startScreenParticles = [];

        // Create gentle floating particles
        for (let i = 0; i < 25; i++) {
            this.startScreenParticles.push({
                x: Math.random() * this.canvas.width,
                y: Math.random() * this.canvas.height,
                vx: (Math.random() - 0.5) * 15, // Gentle horizontal drift
                vy: -10 - Math.random() * 15,     // Upward movement
                size: 2 + Math.random() * 4,
                color: ['#FF6B9D', '#95E1D3', '#FFE66D', '#4ECDC4', '#B88DC4'][Math.floor(Math.random() * 5)],
                alpha: 0.3 + Math.random() * 0.4,
                life: Math.random() * Math.PI * 2, // For sine wave motion
                speed: 0.5 + Math.random() * 1.5
            });
        }
    }

    updateStartScreenParticles(deltaTime) {
        this.startScreenParticles.forEach(p => {
            // Update position with gentle sine wave motion
            p.x += p.vx * deltaTime;
            p.y += p.vy * deltaTime;
            p.life += deltaTime * p.speed;

            // Add subtle sine wave to x movement
            p.x += Math.sin(p.life) * 0.5;

            // Wrap around screen edges
            if (p.x < -10) p.x = this.canvas.width + 10;
            if (p.x > this.canvas.width + 10) p.x = -10;
            if (p.y < -10) p.y = this.canvas.height + 10;
            if (p.y > this.canvas.height + 10) {
                // Reset particle when it goes off screen
                p.x = Math.random() * this.canvas.width;
                p.y = this.canvas.height + 10;
                p.vy = -10 - Math.random() * 15;
            }
        });
    }

    // Helper to convert quantity to qualitative adjective
    getQuantityAdjective(count) {
        if (count <= 3) return 'many';
        if (count <= 8) return 'lots of';
        return 'a great many';
    }

    // Helper to draw animated underline
    drawAnimatedUnderline(x, y, width, time) {
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);

        // Create wavy underline with animation
        const segments = 20;
        const amplitude = 2;
        const frequency = 3;

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const waveX = x + width * t;
            const waveY = y + Math.sin(time * 2 + t * frequency) * amplitude;
            this.ctx.lineTo(waveX, waveY);
        }

        this.ctx.stroke();
    }

    // Helper to lighten a hex color
    lightenColor(hex, percent) {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.min(255, (num >> 16) + Math.round(255 * percent / 100));
        const g = Math.min(255, ((num >> 8) & 0x00FF) + Math.round(255 * percent / 100));
        const b = Math.min(255, (num & 0x0000FF) + Math.round(255 * percent / 100));
        return '#' + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1);
    }

    // Draw grief overlay - a semi-transparent darkening layer
    // This creates a desaturated/darkened effect for unprocessed grief
    drawGriefOverlay() {
        this.ctx.save();
        // Increased intensity - darker overlay for more visible effect
        // Each unprocessed death adds 20% desaturation, max 50%
        // Scale alpha more aggressively for better visibility
        const alpha = this.currentDesaturation * 1.2; // Increased from 0.7 to 1.2
        // Use darker, more muted colors for stronger effect
        this.ctx.fillStyle = `rgba(60, 60, 80, ${Math.min(alpha, 0.6)})`; // Darker grey-blue, capped at 60% opacity
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.restore();
    }

    endGame() {
        this.state = 'ended';
        this.endScreenShownTime = performance.now();
        // Clear all input keys to prevent held keys from immediately triggering return to start
        Input.keys = {};
        
        // Send analytics event: game finished with stats
        if (typeof gtag !== 'undefined') {
            const actualAge = this.player.getActualAge();
            const ageRounded = Math.round(actualAge);
            const experienceCount = this.player.experiences.length;
            
            gtag('event', 'game_end', {
                'event_category': 'gameplay',
                'years_lived': ageRounded,
                'experience_count': experienceCount
            });
        }
    }

    draw() {
        // Draw tile map (this also clears the canvas)
        this.tileMap.draw(this.ctx, this.camera);

        // Draw experiences
        this.experiences.forEach(exp => exp.draw(this.ctx, this.camera));

        // Draw graves
        this.graves.forEach(grave => grave.draw(this.ctx, this.camera));

        // Draw grave memory text (on top of graves)
        this.graves.forEach(grave => grave.drawMemory(this.ctx, this.camera));

        // Draw encounters
        this.encounters.forEach(enc => enc.draw(this.ctx, this.camera));

        // Draw trail (memories and people)
        // Sort living people: partner first, then children, then parents
        const sortedPeople = [...this.player.livingPeople].sort((a, b) => {
            // Partner (Taylor) comes first
            if (a.type === 'partner' && b.type !== 'partner') return -1;
            if (a.type !== 'partner' && b.type === 'partner') return 1;
            
            // Children come after partner
            if (a.type === 'child' && b.type === 'parent') return -1;
            if (a.type === 'parent' && b.type === 'child') return 1;
            
            // Maintain relative order within same type
            return 0;
        });
        
        // Draw memories first (furthest back), then living people
        this.player.memories.forEach(mem => mem.draw(this.ctx, this.camera));
        sortedPeople.forEach(person => person.draw(this.ctx, this.camera));

        // Apply grief desaturation overlay (before drawing Jane so she stays full color)
        if (this.currentDesaturation > 0.01 && this.state === 'playing') {
            this.drawGriefOverlay();
        }

        // Draw player (Jane stays full color - drawn after overlay)
        this.player.draw(this.ctx, this.camera);

        // Draw particles (experience collection effects)
        this.drawParticles();

        // Apply blur effect
        this.applyBlur();

        // Draw UI
        this.drawUI();

        // Draw start screen
        if (this.state === 'start') {
            this.drawStartScreen();
            // Draw mute button on start screen (before return)
            this.drawMuteButton();
            return; // Don't draw game elements on start screen
        }

        // Draw collecting indicator
        if (this.player.isCollecting) {
            this.drawCollecting();
        }

        // Special moment: clear when expired
        let specialMomentActive = false;
        if (this.lastSpecialMoment) {
            const t = (Date.now() - this.lastSpecialMomentTime) / 1000;
            if (t < 5) specialMomentActive = true;
            else { this.lastSpecialMoment = null; this.lastSpecialMomentTime = null; }
        }

        // Draw experience collected notification (move down if special moment is showing)
        const expOffset = specialMomentActive ? 70 : 0;
        if (this.player.lastCollectedExperience) {
            const timeSince = (Date.now() - this.player.lastCollectedTime) / 1000;
            if (timeSince < 3) this.drawExperienceNotification(expOffset);
        }

        // Draw special moment notification (wedding, family)
        if (specialMomentActive) this.drawSpecialMomentNotification();

        // Draw death notification (gentle message when family passes)
        this.drawDeathNotification();

        // Draw achievement notification (experience milestones, anniversaries)
        this.drawAchievementNotification();

        // Draw end screen if game ended
        if (this.state === 'ended') {
            this.drawEndScreen();
        }

        // Draw mute button last (always visible on top, top right)
        this.drawMuteButton();
    }

    applyBlur() {
        if (this.player.blurLevel === 0) return;

        const blurAmount = this.player.blurLevel * 2;
        this.ctx.filter = `blur(${blurAmount}px)`;

        // Apply slight desaturation at higher blur levels
        if (this.player.blurLevel >= 2) {
            this.ctx.globalAlpha = 0.95;
        }
    }

    drawUI() {
        // Reset filter
        this.ctx.filter = 'none';
        this.ctx.globalAlpha = 1;

        // Draw collected experiences as small icons at top left (only when playing)
        if (this.state === 'playing') {
            this.ctx.save();
            const iconSize = 8;
            const spacing = 22;
            const startX = 15;
            const startY = 20;

            this.player.experiences.forEach((exp, i) => {
            const x = startX + i * spacing;
                const y = startY;

                // Use the experience's actual color
                this.ctx.fillStyle = exp.color;
                this.ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                this.ctx.lineWidth = 1.5;

                this.ctx.beginPath();
                if (exp.type === 'nature') {
                    // Triangle
                    this.ctx.moveTo(x, y - iconSize);
                    this.ctx.lineTo(x - iconSize, y + iconSize);
                    this.ctx.lineTo(x + iconSize, y + iconSize);
                this.ctx.closePath();
                } else if (exp.type === 'knowledge') {
                    // Square
                    this.ctx.rect(x - iconSize, y - iconSize, iconSize * 2, iconSize * 2);
            } else {
                    // Pentagon
                    const sides = 5;
                    const angleStep = (Math.PI * 2) / sides;
                    const startAngle = -Math.PI / 2; // Start from top
                    for (let j = 0; j < sides; j++) {
                        const angle = startAngle + j * angleStep;
                        const px = x + Math.cos(angle) * iconSize;
                        const py = y + Math.sin(angle) * iconSize;
                        if (j === 0) {
                            this.ctx.moveTo(px, py);
                        } else {
                            this.ctx.lineTo(px, py);
                        }
                    }
                    this.ctx.closePath();
                }
                this.ctx.fill();
                this.ctx.stroke();
            });
            this.ctx.restore();
        }

        // Draw "Jane" text at top right (only when playing) - HIDDEN FOR TESTING
        // if (this.state === 'playing') {
        //     this.ctx.save();
        //     this.ctx.font = '18px Arial';
        //     this.ctx.textAlign = 'right';
        //     // Draw outline (black)
        //     this.ctx.strokeStyle = '#000';
        //     this.ctx.lineWidth = 3;
        //     this.ctx.lineJoin = 'round';
        //     this.ctx.miterLimit = 2;
        //     // Draw fill (white)
        //     this.ctx.fillStyle = '#fff';
        //     const x = this.canvas.width - 20;
        //     const y = 30;
        //     this.ctx.strokeText('Jane', x, y);
        //     this.ctx.fillText('Jane', x, y);
        //     this.ctx.restore();
        // }

        // Draw interaction hint at top center (only first time when near interactable)
        if (this.state === 'playing' && !this.hasShownInteractionHint) {
            const isNearInteractable = this.checkIfNearInteractable();
            if (isNearInteractable) {
                this.ctx.save();
                this.ctx.font = '18px Arial';
            this.ctx.textAlign = 'center';
                // Draw outline (black)
                this.ctx.strokeStyle = '#000';
                this.ctx.lineWidth = 3;
                this.ctx.lineJoin = 'round';
                this.ctx.miterLimit = 2;
                // Draw fill (white)
                this.ctx.fillStyle = '#fff';
                const x = this.canvas.width / 2;
                const y = 30;
                const hintText = 'Interact: click or Space/E key';
                this.ctx.strokeText(hintText, x, y);
                this.ctx.fillText(hintText, x, y);
            this.ctx.restore();
            }
        }

        // Debug info (bottom right)
        if (this.debugMode) {
            this.ctx.save();
            this.ctx.font = '14px Arial';
            this.ctx.fillStyle = 'rgba(0,0,0,0.7)';
            this.ctx.fillRect(this.canvas.width - 250, this.canvas.height - 130, 240, 120);
            this.ctx.fillStyle = '#fff';
            this.ctx.textAlign = 'left';
            let y = this.canvas.height - 110;
            // Show actual calendar age (years lived)
            this.ctx.fillText(`Years Lived: ${this.player.age.toFixed(2)}`, this.canvas.width - 240, y);
            y += 18;
            this.ctx.fillText(`Remaining Life: ${this.player.remainingLife.toFixed(2)}`, this.canvas.width - 240, y);
            y += 18;
            // Calculate life depletion rate (years per second)
            const baseAgingRate = CONFIG.BASE_LIFESPAN / CONFIG.GAMEPLAY_DURATION;
            const agingRateModifier = this.player.getAgingRateModifier();
            const lifeDepletionRate = baseAgingRate / (1 + agingRateModifier);
            this.ctx.fillText(`Life Depletion: ${lifeDepletionRate.toFixed(3)}/sec`, this.canvas.width - 240, y);
            y += 18;
            this.ctx.fillText(`Aging Modifier: ${agingRateModifier.toFixed(3)}`, this.canvas.width - 240, y);
            y += 18;
            this.ctx.fillText(`Experience Bonus: ${this.player.experienceAgingBonus.toFixed(3)}`, this.canvas.width - 240, y);
            y += 18;
            this.ctx.fillText(`Graves: ${this.graves.length}`, this.canvas.width - 240, y);
            this.ctx.restore();
        }
    }

    drawStartScreen() {
        this.ctx.save();

        // Draw background - lighter version of Jane's color (#FF9F7A -> #FFE4D4)
        this.ctx.fillStyle = '#FFE4D4'; // Light coral-peach
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw floating particles
        this.ctx.globalAlpha = 0.6;
        this.startScreenParticles.forEach(p => {
            this.ctx.fillStyle = p.color;
            this.ctx.beginPath();
            this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            this.ctx.fill();
        });
        this.ctx.globalAlpha = 1;

        // Add additional floating sparkles
        this.drawStartScreenSparkles();

        // Add floating geometric shapes
        this.drawStartScreenShapes();

        // Add subtle gradient overlay
        this.drawStartScreenGradient();

        const time = Date.now() / 1000;

        this.ctx.fillStyle = '#333';
        this.ctx.textAlign = 'center';

        // Draw big "Jane" title with gentle breathing animation
        const janeScale = 1 + Math.sin(time * 1.5) * 0.02;
        this.ctx.save();
        this.ctx.translate(this.canvas.width / 2, this.canvas.height / 2 - 80);
        this.ctx.scale(janeScale, janeScale);
        this.ctx.font = 'bold 72px Georgia';

        // Add subtle glow to Jane
        this.ctx.shadowColor = '#FF6B9D';
        this.ctx.shadowBlur = 8;
        this.ctx.fillText('Jane', 0, 0);
        this.ctx.shadowBlur = 0;
        this.ctx.restore();

        // Below Jane: "by" with gentle fade
        this.ctx.font = '16px Georgia';
        const byAlpha = 0.7 + Math.sin(time * 2) * 0.2;
        this.ctx.globalAlpha = byAlpha;
        this.ctx.fillText('by', this.canvas.width / 2, this.canvas.height / 2 - 20);
        this.ctx.globalAlpha = 1;

        // Below that: "candevor" with subtle movement
        const candevorOffset = Math.sin(time * 1.2) * 0.5;
        this.ctx.font = '24px Georgia';
        this.ctx.fillText('candevor', this.canvas.width / 2, this.canvas.height / 2 + 45 + candevorOffset);

        // Separator line between candevor and controls with fade
        const lineAlpha = 0.5 + Math.sin(time * 1.8) * 0.3;
        this.ctx.strokeStyle = `rgba(153, 153, 153, ${lineAlpha})`;
        this.ctx.lineWidth = 1;
        const lineY = this.canvas.height / 2 + 95; // Moved down from 72 to 95
        this.ctx.beginPath();
        this.ctx.moveTo(this.canvas.width / 2 - 80, lineY);
        this.ctx.lineTo(this.canvas.width / 2 + 80, lineY);
        this.ctx.stroke();

        // Draw "Controls" heading
        const controlsAlpha = 0.8 + Math.sin(time * 2.5) * 0.15;
        this.ctx.globalAlpha = controlsAlpha;
        this.ctx.font = '18px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('game controls', this.canvas.width / 2, this.canvas.height / 2 + 120);

        // Draw tutorial text with gentle fade-in effect
        const tutorialAlpha = 0.8 + Math.sin(time * 2.5) * 0.15;
        this.ctx.globalAlpha = tutorialAlpha;
        this.ctx.font = '16px Arial'; // Smaller font
        this.ctx.fillText('Walk: move with mouse or WASD/arrow keys ', this.canvas.width / 2, this.canvas.height / 2 + 148);
        this.ctx.font = '16px Arial'; // Smaller font
        this.ctx.fillText('Interact: click or Space/E key', this.canvas.width / 2, this.canvas.height / 2 + 178);
        this.ctx.font = '16px Arial'; // Smaller font
        this.ctx.fillText('Exit: right-click or Esc key', this.canvas.width / 2, this.canvas.height / 2 + 208);

        // Add subtle animated underlines
        this.ctx.strokeStyle = `rgba(255, 107, 157, ${tutorialAlpha * 0.6})`;
        this.ctx.lineWidth = 1;
        this.ctx.lineCap = 'round';

        // Underline for first tutorial line
        const line1Width = this.ctx.measureText('Walk: move with mouse or WASD/arrow keys').width;
        const line1X = this.canvas.width / 2 - line1Width / 2;
        const line1Y = this.canvas.height / 2 + 156; // Adjusted for new spacing
        this.drawAnimatedUnderline(line1X, line1Y, line1Width, time);

        // Underline for second tutorial line
        const line2Width = this.ctx.measureText('Interact: click or Space/E key').width;
        const line2X = this.canvas.width / 2 - line2Width / 2;
        const line2Y = this.canvas.height / 2 + 186;
        this.drawAnimatedUnderline(line2X, line2Y, line2Width, time + 0.5);

        // Underline for third tutorial line
        const line3Width = this.ctx.measureText('Exit: right-click or Esc key').width;
        const line3X = this.canvas.width / 2 - line3Width / 2;
        const line3Y = this.canvas.height / 2 + 216;
        this.drawAnimatedUnderline(line3X, line3Y, line3Width, time + 1);

        this.ctx.globalAlpha = 1;

        this.ctx.restore();
    }

    drawCollecting() {
        const progress = this.player.collectTimer / this.player.collectDuration;
        const barWidth = 280;
        const barHeight = 10;
        const barX = this.canvas.width / 2 - barWidth / 2;
        const barY = this.canvas.height - 60;
        const radius = 6;

        this.ctx.save();

        // Determine color based on interaction type
        let fillColor = '#4ECDC4'; // Default teal-green
        if (this.player.collectingExperience) {
            fillColor = this.player.collectingExperience.color || '#4ECDC4';
        } else if (this.player.collectingEncounter) {
            fillColor = this.player.collectingEncounter.color || '#7DCEA0';
        } else if (this.player.collectingGrave) {
            fillColor = '#4ECDC4'; // Cool green for graves
        }

        // Drop shadow
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        this.ctx.shadowBlur = 8;
        this.ctx.shadowOffsetY = 2;

        // Background (rounded rectangle)
        this.ctx.fillStyle = 'rgba(240, 240, 245, 0.9)';
        this.ctx.beginPath();
        this.ctx.moveTo(barX + radius, barY);
        this.ctx.lineTo(barX + barWidth - radius, barY);
        this.ctx.quadraticCurveTo(barX + barWidth, barY, barX + barWidth, barY + radius);
        this.ctx.lineTo(barX + barWidth, barY + barHeight - radius);
        this.ctx.quadraticCurveTo(barX + barWidth, barY + barHeight, barX + barWidth - radius, barY + barHeight);
        this.ctx.lineTo(barX + radius, barY + barHeight);
        this.ctx.quadraticCurveTo(barX, barY + barHeight, barX, barY + barHeight - radius);
        this.ctx.lineTo(barX, barY + radius);
        this.ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
        this.ctx.closePath();
        this.ctx.fill();

        // Progress fill (rounded rectangle)
        if (progress > 0) {
            const progressWidth = barWidth * progress;
            this.ctx.shadowColor = 'transparent';
            this.ctx.shadowBlur = 0;
            this.ctx.shadowOffsetY = 0;
            
            // Gradient for progress bar
            const grad = this.ctx.createLinearGradient(barX, barY, barX, barY + barHeight);
            grad.addColorStop(0, this.lightenColor(fillColor, 20));
            grad.addColorStop(1, fillColor);
            this.ctx.fillStyle = grad;
            
            this.ctx.beginPath();
            this.ctx.moveTo(barX + radius, barY);
            this.ctx.lineTo(barX + progressWidth - radius, barY);
            if (progressWidth >= radius) {
                this.ctx.quadraticCurveTo(barX + progressWidth, barY, barX + progressWidth, barY + radius);
                this.ctx.lineTo(barX + progressWidth, barY + barHeight - radius);
                this.ctx.quadraticCurveTo(barX + progressWidth, barY + barHeight, barX + progressWidth - radius, barY + barHeight);
            }
            this.ctx.lineTo(barX + radius, barY + barHeight);
            this.ctx.quadraticCurveTo(barX, barY + barHeight, barX, barY + barHeight - radius);
            this.ctx.lineTo(barX, barY + radius);
            this.ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
            this.ctx.closePath();
            this.ctx.fill();
        }

        // Border - more visible outline
        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.strokeStyle = '#fff'; // Solid white outline for better visibility
        this.ctx.lineWidth = 2; // Thicker outline
        this.ctx.beginPath();
        this.ctx.moveTo(barX + radius, barY);
        this.ctx.lineTo(barX + barWidth - radius, barY);
        this.ctx.quadraticCurveTo(barX + barWidth, barY, barX + barWidth, barY + radius);
        this.ctx.lineTo(barX + barWidth, barY + barHeight - radius);
        this.ctx.quadraticCurveTo(barX + barWidth, barY + barHeight, barX + barWidth - radius, barY + barHeight);
        this.ctx.lineTo(barX + radius, barY + barHeight);
        this.ctx.quadraticCurveTo(barX, barY + barHeight, barX, barY + barHeight - radius);
        this.ctx.lineTo(barX, barY + radius);
        this.ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
        this.ctx.closePath();
        this.ctx.stroke();

        this.ctx.restore();
    }

    drawExperienceNotification(verticalOffset = 0) {
        const timeSince = (Date.now() - this.player.lastCollectedTime) / 1000;
        const opacity = Math.max(0, 1 - (timeSince / 3)); // Fade out over 3 seconds

        this.ctx.save();
        this.ctx.globalAlpha = opacity;

        const boxWidth = 320;
        const boxHeight = 52;
        const boxX = this.canvas.width / 2 - boxWidth / 2;
        const boxY = 100 + verticalOffset;
        const radius = 12;

        // Drop shadow
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
        this.ctx.shadowBlur = 16;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 5;

        // Rounded rectangle
        this.ctx.beginPath();
        this.ctx.moveTo(boxX + radius, boxY);
        this.ctx.lineTo(boxX + boxWidth - radius, boxY);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
        this.ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - radius, boxY + boxHeight);
        this.ctx.lineTo(boxX + radius, boxY + boxHeight);
        this.ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - radius);
        this.ctx.lineTo(boxX, boxY + radius);
        this.ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
        this.ctx.closePath();

        // Gradient fill using the experience's actual color
        const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
        const baseColor = this.player.lastCollectedExperienceColor || '#FF8C6B';
        // Create lighter and darker versions of the color
        grad.addColorStop(0, this.lightenColor(baseColor, 30) + 'f7');
        grad.addColorStop(1, baseColor + 'f2');
        this.ctx.fillStyle = grad;
        this.ctx.fill();

        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.shadowOffsetY = 0;

        // Border
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        this.ctx.lineWidth = 1.5;
        this.ctx.stroke();

        // Experience text only, centered
        this.ctx.fillStyle = '#fff';
        this.ctx.font = '17px Georgia';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(this.player.lastCollectedExperience, this.canvas.width / 2, boxY + boxHeight / 2);

        this.ctx.restore();
    }

    drawSpecialMomentNotification() {
        const timeSince = (Date.now() - this.lastSpecialMomentTime) / 1000;
        let opacity = 1;
        if (timeSince < 0.3) opacity = timeSince / 0.3;
        else if (timeSince > 4.5) opacity = (5 - timeSince) / 0.5;

        this.ctx.save();
        this.ctx.globalAlpha = opacity;

        const boxWidth = 380;
        const boxHeight = 56;
        const boxX = this.canvas.width / 2 - boxWidth / 2;
        const boxY = 100;
        const radius = 12;
        const cx = this.canvas.width / 2;
        const cy = boxY + boxHeight / 2;

        // Gentle scale-in for first 0.3s
        const scale = timeSince < 0.3 ? 0.92 + (timeSince / 0.3) * 0.08 : 1;
        this.ctx.translate(cx, cy);
        this.ctx.scale(scale, scale);
        this.ctx.translate(-cx, -cy);

        // Drop shadow
        this.ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
        this.ctx.shadowBlur = 20;
        this.ctx.shadowOffsetX = 0;
        this.ctx.shadowOffsetY = 6;

        // Rounded rectangle
            this.ctx.beginPath();
        this.ctx.moveTo(boxX + radius, boxY);
        this.ctx.lineTo(boxX + boxWidth - radius, boxY);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
        this.ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
        this.ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - radius, boxY + boxHeight);
        this.ctx.lineTo(boxX + radius, boxY + boxHeight);
        this.ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - radius);
        this.ctx.lineTo(boxX, boxY + radius);
        this.ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
        this.ctx.closePath();

        if (this.lastSpecialMoment === 'wedding') {
            const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
            grad.addColorStop(0, '#f8ece8');
            grad.addColorStop(1, '#ecd4c4');
            this.ctx.fillStyle = grad;
            this.ctx.fill();
            this.ctx.strokeStyle = 'rgba(180, 140, 120, 0.85)';
        } else if (this.lastSpecialMoment === 'firstchild') {
            const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
            grad.addColorStop(0, '#e8f4f8');
            grad.addColorStop(1, '#c4dce8');
            this.ctx.fillStyle = grad;
            this.ctx.fill();
            this.ctx.strokeStyle = 'rgba(120, 160, 180, 0.85)';
        } else {
            const grad = this.ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxHeight);
            grad.addColorStop(0, '#fff8e7');
            grad.addColorStop(1, '#ffe4b8');
            this.ctx.fillStyle = grad;
            this.ctx.fill();
            this.ctx.strokeStyle = 'rgba(200, 160, 100, 0.85)';
        }

        this.ctx.shadowColor = 'transparent';
        this.ctx.shadowBlur = 0;
        this.ctx.shadowOffsetY = 0;
        this.ctx.lineWidth = 2;
            this.ctx.stroke();

        // Decorative circles (sparkles)
        const decY = boxY + boxHeight / 2;
        if (this.lastSpecialMoment === 'wedding') {
            this.ctx.fillStyle = 'rgba(232, 180, 180, 0.95)';
        } else if (this.lastSpecialMoment === 'firstchild') {
            this.ctx.fillStyle = 'rgba(180, 210, 232, 0.95)';
        } else {
            this.ctx.fillStyle = 'rgba(255, 220, 140, 0.95)';
        }
        this.ctx.beginPath();
        this.ctx.arc(boxX - 38, decY, 6, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.beginPath();
        this.ctx.arc(boxX + boxWidth + 38, decY, 6, 0, Math.PI * 2);
        this.ctx.fill();

        // Text
        let text;
        if (this.lastSpecialMoment === 'wedding') {
            text = `Jane and ${GAME_DATA.names.partner}`;
        } else if (this.lastSpecialMoment === 'firstchild') {
            text = 'a mother';
        } else {
            text = 'a family of four';
        }
        this.ctx.fillStyle = '#5c4033';
        this.ctx.font = '20px Georgia';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(text, this.canvas.width / 2, cy);

        this.ctx.restore();
    }

    drawEndScreen() {
        this.ctx.save();

        // Peaceful background with subtle gradient - lighter version of Jane's color
        const bgGrad = this.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
        bgGrad.addColorStop(0, 'rgba(255, 228, 212, 0.95)'); // Light coral-peach (top)
        bgGrad.addColorStop(1, 'rgba(255, 220, 200, 0.95)'); // Slightly darker light coral-peach (bottom)
        this.ctx.fillStyle = bgGrad;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Add subtle gradient overlay (draw first, before particles)
        this.drawEndScreenGradient();

        const time = Date.now() / 1000;

        this.ctx.fillStyle = '#2c3e50'; // Darker, more reflective color
        this.ctx.textAlign = 'center';

        // Title with gentle glow
        this.ctx.save();
        this.ctx.shadowColor = 'rgba(44, 62, 80, 0.3)';
        this.ctx.shadowBlur = 6;
        this.ctx.font = 'bold 36px Georgia';
        this.ctx.fillText('A Life Remembered', this.canvas.width / 2, 70);
        this.ctx.restore();

        let y = 140;

        // Main life summary
        this.ctx.font = 'bold 24px Georgia';
        const actualAge = this.player.getActualAge();
        const ageRounded = Math.round(actualAge);
        this.ctx.fillText(`Jane lived ${ageRounded} years`, this.canvas.width / 2, y);

        // Life description based on experiences
        y += 35;
        this.ctx.font = '18px Georgia';
        const expCount = this.player.experiences.length;
        let lifeDesc;
        if (expCount < 10) {
            lifeDesc = 'a calm, contemplative life';
        } else if (expCount < 20) {
            lifeDesc = 'a balanced life full of moments';
        } else {
            lifeDesc = 'a rich, adventurous life';
        }

        // Add personality based on dominant experience category
        const natureCount = this.player.experiences.filter(e => e.type === 'nature').length;
        const knowledgeCount = this.player.experiences.filter(e => e.type === 'knowledge').length;
        const pleasureCount = this.player.experiences.filter(e => e.type === 'pleasure').length;

        let personalityDesc = '';
        const maxCategory = Math.max(natureCount, knowledgeCount, pleasureCount);
        if (maxCategory >= 3) { // Only show if they have at least 3 in a category
            if (natureCount === maxCategory) {
                personalityDesc = ', a nature lover';
            } else if (knowledgeCount === maxCategory) {
                personalityDesc = ', a seeker of knowledge';
            } else if (pleasureCount === maxCategory) {
                personalityDesc = ', someone who cherished simple pleasures';
            }
        }

        this.ctx.fillText(`She lived ${lifeDesc}${personalityDesc}`, this.canvas.width / 2, y);

        // Loved ones section
        y += 70;
        this.ctx.font = 'bold 22px Georgia';
        this.ctx.fillText('Loved by', this.canvas.width / 2, y);

        y += 35;
        this.ctx.font = '16px Georgia';

        const hasPartner = this.encounters.find(e => e.type === 'partner' && e.accepted);
        const children = this.player.livingPeople.filter(p => p.type === 'child');

        if (hasPartner) {
            // Married: Taylor, [children], and her parents
            let lovedOnes = ['Taylor'];
            children.forEach(child => lovedOnes.push(child.name));
            lovedOnes.push('and her parents');

            // Split into lines if too long
            const lovedText = lovedOnes.join(', ');
            if (lovedText.length > 40) {
                // Split at reasonable point
                const midPoint = lovedText.lastIndexOf(',', lovedText.length / 2);
                const firstLine = lovedText.substring(0, midPoint + 1);
                const secondLine = lovedText.substring(midPoint + 2);
                this.ctx.fillText(firstLine.trim(), this.canvas.width / 2, y);
                y += 25;
                this.ctx.fillText(secondLine.trim(), this.canvas.width / 2, y);
            } else {
                this.ctx.fillText(lovedText, this.canvas.width / 2, y);
            }
        } else {
            // Not married: Free soul
            this.ctx.fillText('A free soul, surrounded by her parents', this.canvas.width / 2, y);
        }

        // Experiences section - only show if player collected any experiences
        const totalExperiences = this.player.experiences.length;
        if (totalExperiences > 0) {
            y += 70;
            this.ctx.font = 'bold 22px Georgia';
            this.ctx.fillText('Cherished', this.canvas.width / 2, y);

            y += 35;
            this.ctx.font = '16px Georgia';

            const expLines = [];
            if (natureCount > 0) expLines.push(`${this.getQuantityAdjective(natureCount)} moments of nature`);
            if (knowledgeCount > 0) expLines.push(`${this.getQuantityAdjective(knowledgeCount)} discoveries of knowledge`);
            if (pleasureCount > 0) expLines.push(`${this.getQuantityAdjective(pleasureCount)} simple pleasures`);

            expLines.forEach((line, i) => {
                this.ctx.fillText(line, this.canvas.width / 2, y + i * 25);
            });
        }

        // Gentle fade in effect for the entire screen
        const fadeProgress = Math.min(1, (Date.now() - this.endScreenShownTime) / 1000);
        if (fadeProgress < 1) {
            this.ctx.globalCompositeOperation = 'destination-in';
            this.ctx.fillStyle = `rgba(255, 255, 255, ${fadeProgress})`;
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.globalCompositeOperation = 'source-over';
        }

        // Draw particles AFTER text so they're visible on top
        // Add subtle floating particles for peaceful atmosphere
        this.drawEndScreenParticles();

        // Add more ethereal particles that rise slowly
        this.drawEndScreenRisingParticles();

        // Add sparkles around the title (similar to start screen but more peaceful)
        this.drawEndScreenSparkles();

        // Add floating geometric shapes (reflective theme)
        this.drawEndScreenShapes();

        this.ctx.restore();
    }

    // Peaceful floating particles for end screen
    drawEndScreenParticles() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.6; // Increased visibility

        // Draw gentle floating particles
        for (let i = 0; i < 15; i++) {
            const x = (this.canvas.width * 0.2) + (Math.sin(time * 0.3 + i) * 50) + (i * 40);
            const y = 100 + (Math.cos(time * 0.2 + i * 0.5) * 30) + (i * 20);
            const size = 2 + Math.sin(time * 1 + i) * 1;

            // More colorful but still peaceful
            this.ctx.fillStyle = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A', '#B0E0E6', '#FFDAB9'][i % 8];
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.ctx.globalAlpha = 1;
    }

    // Additional sparkles for start screen
    drawStartScreenSparkles() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.4;

        // Draw twinkling sparkles around the title area
        for (let i = 0; i < 12; i++) {
            const centerX = this.canvas.width / 2;
            const centerY = this.canvas.height / 2 - 60;
            const angle = (Math.PI * 2 * i / 12) + time * 0.5;
            const radius = 80 + Math.sin(time * 1.5 + i) * 20;
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            const size = 2 + Math.sin(time * 3 + i * 2) * 1.5;

            this.ctx.fillStyle = ['#FFD700', '#FF69B4', '#87CEEB', '#98FB98'][i % 4];
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();

            // Add subtle glow
            this.ctx.shadowColor = this.ctx.fillStyle;
            this.ctx.shadowBlur = 6;
            this.ctx.fill();
            this.ctx.shadowBlur = 0;
        }

        // Additional floating sparkles
        for (let i = 0; i < 8; i++) {
            const x = Math.sin(time * 0.7 + i) * 200 + this.canvas.width / 2;
            const y = Math.cos(time * 0.5 + i) * 100 + this.canvas.height / 2;
            const size = 1.5 + Math.sin(time * 2 + i) * 1;

            this.ctx.fillStyle = '#FFFFFF';
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.ctx.globalAlpha = 1;
    }

    // Floating geometric shapes for visual variety
    drawStartScreenShapes() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.3;

        // Draw some gentle floating shapes
        for (let i = 0; i < 6; i++) {
            const shapeX = (this.canvas.width * 0.1) + Math.sin(time * 0.4 + i * 1.5) * 100;
            const shapeY = (this.canvas.height * 0.2) + Math.cos(time * 0.3 + i) * 80;
            const size = 8 + Math.sin(time * 0.8 + i) * 4;
            const rotation = time * 0.2 + i;

            this.ctx.save();
            this.ctx.translate(shapeX, shapeY);
            this.ctx.rotate(rotation);

            const colors = ['#FF6B9D', '#95E1D3', '#FFE66D', '#B88DC4', '#5DADE2'];
            this.ctx.fillStyle = colors[i % colors.length];
            this.ctx.strokeStyle = colors[i % colors.length];
            this.ctx.lineWidth = 1.5;

            // Alternate between triangles and squares
            if (i % 2 === 0) {
                // Triangle
                this.ctx.beginPath();
                this.ctx.moveTo(0, -size);
                this.ctx.lineTo(-size, size);
                this.ctx.lineTo(size, size);
                this.ctx.closePath();
                this.ctx.fill();
            } else {
                // Square
                this.ctx.strokeRect(-size/2, -size/2, size, size);
            }

            this.ctx.restore();
        }

        this.ctx.globalAlpha = 1;
    }

    // Subtle gradient overlay for depth
    drawStartScreenGradient() {
        const time = Date.now() / 1000;

        // Animated radial gradient for subtle depth
        const gradient = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2,
            0,
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.6
        );

        const alpha1 = 0.05 + Math.sin(time * 0.3) * 0.02;
        const alpha2 = 0.08 + Math.sin(time * 0.5) * 0.03;

        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha1})`);
        gradient.addColorStop(0.5, `rgba(240, 248, 255, ${alpha2})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        this.ctx.globalCompositeOperation = 'overlay';
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.globalCompositeOperation = 'source-over';

        // Add some subtle vignette
        const vignette = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.3,
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.8
        );
        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(1, `rgba(0, 0, 0, ${0.1 + Math.sin(time * 0.2) * 0.05})`);

        this.ctx.globalCompositeOperation = 'multiply';
        this.ctx.fillStyle = vignette;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.globalCompositeOperation = 'source-over';
    }

    // Ethereal rising particles for end screen reflection
    drawEndScreenRisingParticles() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.3;

        // Draw slow-rising particles that evoke reflection and peace
        for (let i = 0; i < 12; i++) {
            const baseX = (this.canvas.width * 0.1) + (i * 60);
            const x = baseX + Math.sin(time * 0.5 + i * 0.7) * 25;
            const y = (this.canvas.height * 0.8) - ((time * 15 + i * 50) % (this.canvas.height * 1.2));
            const size = 1.5 + Math.sin(time * 0.8 + i) * 0.8;

            // More colorful but still peaceful - soft pastels
            const colors = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A', '#B0E0E6', '#FFDAB9'];
            this.ctx.fillStyle = colors[i % colors.length];

            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();

            // Add subtle glow
            this.ctx.shadowColor = colors[i % colors.length];
            this.ctx.shadowBlur = 8;
            this.ctx.fill();
            this.ctx.shadowBlur = 0;
        }

        // Add some floating sparkles near the text
        for (let i = 0; i < 8; i++) {
            const x = this.canvas.width / 2 + Math.sin(time * 0.6 + i) * 120;
            const y = 200 + Math.cos(time * 0.4 + i) * 40 + Math.sin(time * 1.2 + i) * 20;
            const size = 1 + Math.sin(time * 2 + i) * 0.5;

            // More colorful - use varied colors
            const colors = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A'];
            this.ctx.fillStyle = colors[i % colors.length];
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.ctx.globalAlpha = 1;
    }

    // Sparkles for end screen (peaceful, reflective theme)
    drawEndScreenSparkles() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.7; // Increased visibility

        // Draw gentle twinkling sparkles around the title area
        for (let i = 0; i < 10; i++) {
            const centerX = this.canvas.width / 2;
            const centerY = 70; // Near title
            const angle = (Math.PI * 2 * i / 10) + time * 0.3;
            const radius = 100 + Math.sin(time * 1 + i) * 30;
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            const size = 1.5 + Math.sin(time * 2 + i * 2) * 1;

            // More colorful but still peaceful - soft pastels
            const colors = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A', '#B0E0E6', '#FFDAB9'];
            this.ctx.fillStyle = colors[i % colors.length];
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();

            // Add subtle glow
            this.ctx.shadowColor = colors[i % colors.length];
            this.ctx.shadowBlur = 8;
            this.ctx.fill();
            this.ctx.shadowBlur = 0;
        }

        // Additional floating sparkles around content area
        for (let i = 0; i < 12; i++) {
            const x = Math.sin(time * 0.5 + i) * 300 + this.canvas.width / 2;
            const y = Math.cos(time * 0.4 + i) * 150 + this.canvas.height / 2;
            const size = 1 + Math.sin(time * 1.5 + i) * 0.8;

            const colors = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A'];
            this.ctx.fillStyle = colors[i % colors.length];
            this.ctx.beginPath();
            this.ctx.arc(x, y, size, 0, Math.PI * 2);
            this.ctx.fill();
        }

        this.ctx.globalAlpha = 1;
    }

    // Floating geometric shapes for end screen (reflective theme)
    drawEndScreenShapes() {
        const time = Date.now() / 1000;
        this.ctx.globalAlpha = 0.4; // Increased visibility

        // Draw gentle floating shapes (more subtle than start screen)
        for (let i = 0; i < 8; i++) {
            const shapeX = (this.canvas.width * 0.15) + Math.sin(time * 0.3 + i * 1.2) * 150;
            const shapeY = (this.canvas.height * 0.3) + Math.cos(time * 0.25 + i) * 100;
            const size = 6 + Math.sin(time * 0.6 + i) * 3;
            const rotation = time * 0.15 + i;

            this.ctx.save();
            this.ctx.translate(shapeX, shapeY);
            this.ctx.rotate(rotation);

            // More colorful but still peaceful - soft pastels
            const colors = ['#FFB6C1', '#87CEEB', '#DDA0DD', '#98D8C8', '#F0E68C', '#FFA07A', '#B0E0E6', '#FFDAB9'];
            this.ctx.fillStyle = colors[i % colors.length];
            this.ctx.strokeStyle = colors[i % colors.length];
            this.ctx.lineWidth = 1;

            // Alternate between triangles and circles (more organic)
            if (i % 2 === 0) {
                // Triangle
                this.ctx.beginPath();
                this.ctx.moveTo(0, -size);
                this.ctx.lineTo(-size, size);
                this.ctx.lineTo(size, size);
                this.ctx.closePath();
                this.ctx.fill();
            } else {
                // Circle
                this.ctx.beginPath();
                this.ctx.arc(0, 0, size, 0, Math.PI * 2);
                this.ctx.fill();
            }

            this.ctx.restore();
        }

        this.ctx.globalAlpha = 1;
    }

    // Gradient overlay for end screen (peaceful, reflective theme)
    drawEndScreenGradient() {
        const time = Date.now() / 1000;

        // Animated radial gradient for subtle depth
        const gradient = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2,
            0,
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.7
        );

        const alpha1 = 0.03 + Math.sin(time * 0.2) * 0.02;
        const alpha2 = 0.05 + Math.sin(time * 0.4) * 0.03;

        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha1})`);
        gradient.addColorStop(0.5, `rgba(240, 248, 255, ${alpha2})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        this.ctx.globalCompositeOperation = 'overlay';
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.globalCompositeOperation = 'source-over';

        // Add subtle vignette (softer than start screen)
        const vignette = this.ctx.createRadialGradient(
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.4,
            this.canvas.width / 2, this.canvas.height / 2,
            this.canvas.width * 0.9
        );
        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(1, 'rgba(0, 0, 0, 0.15)');

        this.ctx.globalCompositeOperation = 'multiply';
        this.ctx.fillStyle = vignette;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.globalCompositeOperation = 'source-over';
    }

    handleInput() {
        // Update mouse world coordinates continuously
        Input.updateWorldCoordinates();
        
        // Handle start screen
        if (this.state === 'start') {
            // Check if mouse was clicked
            const mouseClicked = Input.consumeClick();
            
            // Check if any movement or interaction key is pressed
            const keyboardPressed = Input.isDown('w') || Input.isDown('a') || Input.isDown('s') || Input.isDown('d') ||
                Input.isDown('arrowup') || Input.isDown('arrowdown') || Input.isDown('arrowleft') || Input.isDown('arrowright') ||
                Input.isDown(' ') || Input.isDown('e');
            
            if (keyboardPressed || mouseClicked) {
                // Set input mode based on what triggered the start
                this.inputMode = mouseClicked ? 'mouse' : 'keyboard';
                this.startGame();
            }
            return;
        }

        // Handle end screen
        if (this.state === 'ended') {
            // Only accept input after 3 seconds delay to prevent accidental clicks during transitions
            const timeSinceEndScreen = performance.now() - this.endScreenShownTime;
            const END_SCREEN_INPUT_DELAY = 3000; // 3 seconds delay
            
            if (timeSinceEndScreen >= END_SCREEN_INPUT_DELAY) {
                // Check if interaction keys are pressed, ESC, or mouse click
                if (Input.isDown(' ') || Input.isDown('e') || Input.isDown('escape') || Input.consumeClick()) {
                    this.returnToStart();
                    // Clear the keys to prevent re-triggering
                    Input.keys[' '] = false;
                    Input.keys['e'] = false;
                    Input.keys['escape'] = false;
                }
            }
            return;
        }

        // TEST MODE: Disabled for user testing builds
        // Debug keys Q, X, and Z have been disabled

        // Return to start screen with Esc
        if (Input.isDown('escape')) {
            this.returnToStart();
            Input.keys['escape'] = false;
        }
        
        // If keyboard keys are pressed, switch to keyboard mode (reactive)
        const keyboardPressed = Input.isDown('arrowright') || Input.isDown('d') || 
                                Input.isDown('arrowleft') || Input.isDown('a') ||
                                Input.isDown('arrowdown') || Input.isDown('s') ||
                                Input.isDown('arrowup') || Input.isDown('w');
        if (keyboardPressed && this.inputMode === 'mouse') {
            this.inputMode = 'keyboard';
        }
        
        // If in keyboard mode and user clicks, switch to mouse mode
        if (this.inputMode === 'keyboard' && Input.consumeClick()) {
            this.inputMode = 'mouse';
        }
        
        // Consume any unused mouse clicks at the end of the frame to prevent stale clicks
        // (clicks that trigger interactions are already consumed in the interaction checks)
        if (Input.mouseClicked && !this.player.isCollecting && this.inputMode === 'mouse') {
            // Only consume if not near any interactable (to allow click to persist if user moves near one)
            if (!this.checkIfNearInteractable()) {
                Input.consumeClick();
            }
        }
    }

    startGame() {
        this.state = 'playing';
        // Reset game if needed
        this.lastTime = performance.now();
        
        // Send analytics event: game started
        if (typeof gtag !== 'undefined') {
            gtag('event', 'game_start', {
                'event_category': 'gameplay',
                'value': 1
            });
        }
        
        // Start music on user interaction (autoplay may be blocked)
        // Only auto-start if muted due to autoplay blocking, NOT if user explicitly muted
        if (this.muted && !this.musicStarted && !this.userExplicitlyMuted) {
            // User interaction allows us to start music - unmute and start
            this.muted = false;
            this.track1.volume = 0.2;
            this.track2.volume = 0.2;
            this.startMusic();
        }
        // If user explicitly muted, respect their choice and don't start music
        // If music is already playing (autoplay succeeded), do nothing
    }

    startMusic() {
        // Try to start music if not already playing
        if (!this.musicStarted && !this.userExplicitlyMuted && this.track1.paused && this.track2.paused) {
            this.track1.play().then(() => {
                this.musicStarted = true;
                // Only update muted state if user didn't explicitly mute
                if (!this.userExplicitlyMuted) {
                    this.muted = false;
                }
            }).catch(err => {
                console.log('Could not start music:', err);
                // If play fails, keep muted state (but don't override user's explicit choice)
                if (!this.userExplicitlyMuted) {
                    this.muted = true;
                }
            });
        }
    }

    checkIfNearInteractable() {
        // Check if near any experience
        for (let exp of this.experiences) {
            if (exp.canInteract(this.player.x, this.player.y)) {
                return true;
            }
        }

        // Check if near any grave
        for (let grave of this.graves) {
            if (grave.canInteract(this.player.x, this.player.y)) {
                return true;
            }
        }

        // Check if near any encounter
        for (let encounter of this.encounters) {
            if (encounter.canInteract(this.player.x, this.player.y)) {
                // Check if encounter should be available
                // Children require partner, but can be interacted with in any order
                if (encounter.type === 'child1' || encounter.type === 'child2') {
                    const hasPartner = this.player.livingPeople.some(p => p.type === 'partner');
                    if (!hasPartner) continue;
                    // No longer require child1 before child2 - can interact with either in any order
                }
                return true;
            }
        }

        return false;
    }

    returnToStart() {
        // Reset game state
        this.state = 'start';
        
        // Reset screen timing
        this.endScreenShownTime = null;
        this.startScreenShownTime = performance.now();
        this.lastSpecialMoment = null;
        this.lastSpecialMomentTime = null;
        this.particles = [];
        this.deathNotification = null;
        this.deathNotificationTime = null;
        
        // Reset player (accounting for border tiles)
        const borderSize = 80; // Tile size
        this.player = new Player(borderSize + 20, CONFIG.WORLD_HEIGHT / 2);
        
        // Clear and reinitialize game elements
        this.encounters = [];
        this.initializeEncounters();
        this.experiences = [];
        this.initializeExperiences();
        this.graves = [];
        this.initializeParents();
        
        // Reset death tracking
        this.parent1Died = false;
        this.parent2Died = false;
        this.partnerDied = false;
        
        // Re-randomize death ages for new game
        this.initializeDeathAges();
        
        // Reset UI state
        this.hasShownInteractionHint = false;
        
        // Reset anniversary tracking
        this.partnerJoinedAge = null;
        this.triggeredAnniversaries = new Set();

        // Reset experience achievements
        this.triggeredAchievements = new Set();

        // Reset achievement notification
        this.achievementNotification = null;
        this.achievementNotificationTime = null;
        this.achievementNotificationType = null;

        // Reset grave visiting (handled by unified interaction system)
        this.graveVisitParticleTimer = 0;

        // Reset desaturation
        this.targetDesaturation = 0;
        this.currentDesaturation = 0;
        
        // Reset camera
        this.camera.follow(this.player.x, this.player.y);
        
        this.lastTime = performance.now();
    }
}

// UI System
class UI {
    constructor(game) {
        this.game = game;
    }

    update() {
        // Experience counter is now drawn on canvas, not in sidebar
    }
}

// Main entry point
let game;
let ui;

function init() {
    // Get canvas
    const canvas = document.getElementById('game');

    // Create game
    game = new Game(canvas);

    // Initialize input (after game is created so we can pass camera)
    Input.init(canvas, game.camera);

    // Create UI
    ui = new UI(game);

    // Start game loop
    gameLoop();
}

function gameLoop() {
    // Handle input
    game.handleInput();

    // Update game
    game.update();

    // Draw game
    game.draw();

    // Update UI
    ui.update();

    // Continue loop
    requestAnimationFrame(gameLoop);
}

// Start when page loads
window.addEventListener('load', init);

